import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldLabel,
  getFieldName,
  getFieldOptions,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormInput,
  FormRadioButton,
  FormSelect,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";
import { Tooltip } from "react-tooltip";
import ReactDOMServer from "react-dom/server";

const DomainExpandDiv = ({
  children,
  title,
  subTitle,
  expandStatus,
  preserveChildren,
  score,
  previousScore,
  progress,
  ...rest
}) => {
  const [expanded, setExpanded] = useState(expandStatus);

  useEffect(() => {
    setExpanded(expandStatus);
  }, [expandStatus]);

  return (
    <div
      className={`container bg-secondary px-3 border-bottom${
        expanded ? " pb-3" : ""
      }`}
      {...rest}
    >
      <div className="row py-3">
        <div className="col-6">
          <span className="h4">
            <i
              title={expanded ? "Collapse" : "Expand"}
              className={`p-1 text-primary ${
                expanded ? "fas fa-chevron-up" : "fas fa-chevron-down"
              }`}
              onClick={() => setExpanded(!expanded)}
            ></i>
            {title}
          </span>
        </div>
        {previousScore !== undefined ? (
          <div className="col-2 text-center">
            <div className="fw-bold text-white">
              Previous score:
              <br />
              {previousScore}
            </div>
          </div>
        ) : null}
        <div className="col text-center">
          {score ? (
            <div className="fw-bold text-white">
              Score:
              <br />
              {score}
            </div>
          ) : null}
        </div>
        <div className="col-2">
          <div
            className="progress"
            role="progressbar"
            aria-label={`${title} Progress Bar`}
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
            style={{ height: "1.5rem" }}
          >
            <div
              className="progress-bar text-bg-info fw-bold overflow-visible"
              style={{ width: `${progress}%` }}
            >
              <span className="ms-2">{progress}%</span>
            </div>
          </div>
        </div>
      </div>
      {preserveChildren ? (
        <span className={`${!expanded ? "d-none" : ""}`}>{children}</span>
      ) : expanded ? (
        children
      ) : null}
    </div>
  );
};

const definitions = {
  LFDFamily: {
    details:
      "This rates the youth’s relationships with those who are in the youth’s family. It is recommended that the description of family should come from the youth’s perspective (i.e. who the youth describes as their family). In the absence of this information, consider biological and adoptive relatives and their significant others with whom the youth is still in contact. Foster families should only be considered if they have made a significant commitment to the youth. For youth involved with child welfare, family refers to the person(s) fulfilling the permanency plan. When rating this item, take into account the relationship the youth has with the youth’s family as well as the relationship of the family as a whole.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of problems in relationships with family members, and/or youth is doing well in relationships with family members. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. History or suspicion of problems. Youth might be doing adequately in relationships with family members, although some problems may exist. For example, some family members may have problems in their relationships with youth. Arguing may be common but does not result in major problems. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth is having problems with parents, siblings and/or other family members that are impacting the youth’s functioning. Frequent arguing, difficulty maintaining positive relationships may be observed. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is having severe problems with parents, siblings, and/or other family members. This would include problems of domestic violence, absence of any positive relationships, etc. ",
      },
    ],
    considerations:
      "Is there conflict in the family relationship that requires resolution? Is treatment required to restore or develop positive relationship in the family?",
  },
  LFDSituation: {
    details:
      "This item refers to how the youth is functioning in the youth’s current living arrangement, which could be with a relative, in a foster home, etc. This item should exclude respite, brief detention/jail, and brief medical and psychiatric hospitalization.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of problem with functioning in current living environment. Youth and caregivers feel comfortable dealing with issues that come up in day-to-day life. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth experiences mild problems with functioning in current living situation. Caregivers express some concern about youth’s behavior in living situation, and/or youth and caregiver have some difficulty dealing with issues that arise in daily life. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth has moderate to severe problems with functioning in current living situation. Youth’s difficulties in maintaining appropriate behavior in this setting are creating significant problems for others in the residence. Youth and caregivers have difficulty interacting effectively with each other much of the time. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth has profound problems with functioning in current living situation. Youth is at immediate risk of being removed from living situation due to problematic behaviors. ",
      },
    ],
    considerations:
      "How has the youth been behaving and getting along with others in the current living situation?",
  },
  LFDSocial: {
    details:
      "This item rates social skills and relationships. It includes age appropriate behavior and the ability to make and sustain relationships. Social functioning is different from Interpersonal (Strengths) in that functioning is a description of how the youth is doing currently. Strengths are longer-term assets.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of problems and/or youth has developmentally appropriate social functioning  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history or suspicion of problems in social relationships. Youth is having some difficulty interacting with others and building and/or maintaining relationships. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth is having some problems with social relationships that interfere with functioning in other life domains. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is experiencing significant disruptions in social relationships. Youth may have no friends or have constant conflict in relations with others, or have maladaptive relationships with others. The quality of the youth’s social relationships presents imminent danger to the youth’s safety, health, and/or development. ",
      },
    ],
    considerations:
      "Is the youth pleasant and likeable? Do same age peers like the youth? Do you feel that the youth can act appropriately in social settings?",
  },
  LFDRecreational: {
    details:
      "This item rates the youth’s access to and use of leisure activities",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of any problems with recreational functioning. Youth has access sufficient activities that the youth enjoys. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth is doing adequately with recreational activities although some problems may exist. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth is having moderate problems with recreational activities. Youth may experience some problems with effective use of leisure time. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth has no access to or interest in recreational activities. Youth has significant difficulties making use of leisure time. ",
      },
    ],
    considerations:
      "Does the youth have things that they like to do with free time? Things that give the youth pleasure? Activities that are a positive use of the youth’s extra time? Does the youth often claim to be bored or have nothing to do?",
  },
  LFDDevelopmental: {
    details:
      "This item describes the youth’s development as compared to standard developmental milestones, as well as rates the presence of any developmental or intellectual disabilities. It includes Intellectual Developmental Disorder (IDD) and Autism Spectrum Disorders. Rate the item depending on the significance of the disability and the related level of impairment in personal, social, family, school, or occupational functioning.*A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the Developmental Needs Module (pg. 48).*",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of developmental delay and/or youth has no developmental problems or intellectual disability. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There are concerns about possible developmental delay. Youth may have low IQ, a documented delay, or documented borderline intellectual disability (i.e. FSIQ 70-85). Mild deficits in adaptive functioning are indicated. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth has mild developmental delays (e.g., deficits in social functioning, inflexibility of behavior causing functional problems in one or more settings) and/or mild to moderate Intellectual Disability/Intellectual Disability Disorder. (If available, FSIQ 55-69.) IDD impacts communication, social functioning, daily living skills, judgment, and/or risk of manipulation by others. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth has severe to profound intellectual disability (FSIQ, if available, less than 55) and/or Autism Spectrum Disorder with marked to profound deficits in adaptive functioning in one or more areas: communication, social participation and independent living across multiple environments. ",
      },
    ],
    considerations:
      "Does the youth’s growth and development seem healthy? Has the youth reached appropriate developmental milestones (such as walking, talking)? Has anyone ever mentioned that the youth may have developmental problems? Has the youth developed like other same age peers?",
  },
  LFDJob: {
    details:
      "If the youth is working, this item describes their functioning in a job setting.",
    options: [
      {
        _id: "N",
        name: "N/A",
        description:
          "(N/A) = Not applicable. Youth is not currently working nor recently employed. ",
      },
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of any problems in work environment. Youth is excelling in a job environment. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth has some mild problems at work (e.g., tardiness, conflict). Youth is functioning adequately in a job environment. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth has moderate problems at work. Youth has problems with development of vocational or prevocational skills. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth has severe problems at work in terms of attendance, performance or relationships. Youth may have recently lost a job. ",
      },
    ],
    considerations:
      "Is the youth able to meet expectations at work? Does the youth have regular conflict at work? Is the youth timely and able to complete responsibilities?",
  },
  LFDLegal: {
    details:
      "This item indicates the individual’s level of involvement with the juvenile justice system. Family involvement with the courts is not rated here—only the identified individual’s involvement is relevant to this rating. Issues of family involvement in the justice system are not rated here.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth has no known legal difficulties or involvement with the court system. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth has a history of legal problems (e.g., status offenses such as juvenile/family conflict, in county runaway, truancy, petty offenses) but currently is not involved with the legal system; or immediate risk of involvement with the legal system. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth has some legal problems and is currently involved in the legal system due to moderate delinquent behaviors (misdemeanors such as offenses against persons or property, drug-related offenses, underage drinking). ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth has serious current or pending legal difficulties that place him/her at risk for a court ordered out of home placement, or incarceration (ages 18 to 21) such as serious offenses against person or property (e.g., robbery, aggravated assault, possession with intent to distribute controlled substances, 1st or 2nd degree offenses). ",
      },
    ],
    considerations:
      "Has the youth ever admitted that the youth has broken the law? Has the youth ever been arrested? Has the youth ever been in detention?",
  },
  LFDMedical: {
    details:
      "This rating describes both health problems and chronic/acute physical conditions or impediments.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence that the youth has any medical or physical problems, and/or the youth is healthy. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth has mild, transient or well-managed physical or medical problems. These include well managed chronic conditions like juvenile diabetes or asthma. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth has serious medical or physical problems that require medical treatment or intervention. Or youth has a chronic illness or a physical challenge that requires ongoing medical intervention. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth has life-threatening illness or medical/physical condition. Immediate and/or intense action should be taken due to imminent danger to youth’s safety, health, and/or development. ",
      },
    ],
    considerations:
      "Does the youth have anything that limits the youth’s physical activities? How much does this interfere with the youth’s life?",
  },
  LFDSexual: {
    details:
      "This item looks at broad issues of sexual development including developmentally inappropriate sexual behavior or sexual concerns, and the reactions of others to any of these factors. The youth’s sexual orientation, gender identity or expression (SOGIE) could be rated here only if they are leading to difficulties. Sexually abusive behaviors are rated elsewhere.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of issues with sexual development. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. History or suspicion of problems with sexual development, but does not interfere with functioning in other life domains. May include the youth’s concerns about sexual orientation, gender identity and expression (SOGIE), or anxiety about the reaction of others. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Moderate to serious problems with sexual development that interferes with the youth’s life functioning in other life domains. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Severe problems with sexual development. This would include very frequent risky sexual behavior, sexual aggression, or victim of sexual exploitation. ",
      },
    ],
    considerations:
      "Are there concerns about the youth’s healthy sexual development? Is the youth sexually active? Does the youth have less/more interest in sex than other same age peers?",
  },
  LFDSleep: {
    details:
      "This item rates the youth’s sleep patterns. This item is used to describe any problems with sleep, regardless of the cause including difficulties falling asleep or staying asleep as well as sleeping too much. Both bedwetting and nightmares should be considered sleep issues.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth gets a full night’s sleep each night. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth has some problems sleeping. Generally, youth gets a full night’s sleep but at least once a week problems arise. This may include occasionally awakening or bed wetting or having nightmares. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth is having problems with sleep. Sleep is often disrupted and youth seldom obtains a full night of sleep. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is generally sleep deprived. Sleeping is almost always difficult and the youth is not able to get a full night’s sleep. ",
      },
    ],
    considerations:
      "Does the youth appear rested? Is the youth often sleepy during the day? Does the youth have frequent nightmares or difficulty sleeping? How many hours does the youth sleep each night?",
  },
  LFDBehavior: {
    details:
      "This item rates the behavior of the youth in school or school-like settings",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of behavioral problems at school, OR youth is behaving well in school. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth is behaving adequately in school although some behavior problems exist. Behavior problems may be related to either relationship with either teachers or peers. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth’s behavior problems are interfering with functioning at school. The youth is disruptive and may have received sanctions including suspensions. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is having severe problems with behavior in school. The youth is frequently or severely disruptive. School placement may be in jeopardy due to behavior. ",
      },
    ],
    considerations:
      "How is the youth behaving in school? Has the youth had any detentions or suspensions? Has the youth needed to go to an alternative placement?",
  },
  LFDAttendance: {
    details:
      "This items rates issues of attendance. If school is not in session, rate the last 30 days when school was in session.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth attends school regularly. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth has a history of attendance problems, OR youth has some attendance problems but generally goes to school. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth’s problems with school attendance are interfering with academic progress. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is generally absent from school. ",
      },
    ],
    considerations:
      "Does the youth have any difficulty attending school? Is the youth on time to school? How many times a week is the youth absent? Once the youth arrives at school, does the youth stay for the rest of the day?",
  },
  LFDAchievement: {
    details:
      "This item rates the youth’s grades or level of academic achievement.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of issues in school achievement and/or youth is doing well in school. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth is doing adequately in school although some problems with achievement exist. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth is having moderate problems with school achievement. The youth may be failing some subjects. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is having severe achievement problems. The youth may be failing most subjects or has been retained (held back) a grade level. Youth might be more than one year behind same-age peers in school achievement. ",
      },
    ],
    considerations:
      "How are the youth’s grades? Is the youth having difficulty with any subjects? Is the youth at risk for failing any classes or repeating a grade?",
  },
  LFDDecisions: {
    details:
      "This item describes the youth’s age-appropriate decision making process and understanding of choices and consequences.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of problems with judgment or decision making that result in harm to development and/or well-being. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history or suspicion of problems with judgment in which the youth makes decisions that are in some way harmful to the youth’s development and/or well-being. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Problems with judgment in which the youth makes decisions that are in some way harmful to the youth’s development and/or well-being. As a result, more supervision is required than expected for the youth’s age. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth makes decisions that would likely result in significant physical harm to self or others. Therefore, youth requires intense and constant supervision, over and above that expected for the youth’s age. ",
      },
    ],
    considerations:
      "How is the youth’s judgment and ability to make good decisions? Does the youth typically make good choices for the youth?",
  },
  SDFamily: {
    details:
      "This item refers to the presence of a sense of family identity as well as love and communication among family members. Even families who are struggling often have a firm foundation that consists of a positive sense of family and strong underlying love and commitment to each other. These are the constructs this strength is intended to identify. As with Family Functioning, the definition of family comes from the youth’s perspective (i.e., who the youth describes as their family). If this information is not known, then we recommend a definition of family that includes biological/adoptive relatives and their significant others with whom the youth is still in contact.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Family has strong relationships and significant family strengths. This level indicates a family with much love and respect for one another. There is at least one family member who has a strong loving relationship with the youth and is able to provide significant emotional or concrete support. Youth is fully included in family activities.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Family has some good relationships and good communication. Family members are able to enjoy each other’s company. There is at least one family member who has a strong, loving relationship with the youth and is able to provide limited emotional or concrete support.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Family needs some assistance in developing relationships and/or communications. Family members are known, but currently none are able to provide emotional or concrete support.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. Family needs significant assistance in developing relationships and communications, or youth has no identified family. Youth is not included in normal family activities.  ",
      },
    ],
    considerations:
      "Does the youth have good relationships with any family member? Is there potential to develop positive family relationships? Is there a family member that the youth can go to in time of need for support? That can advocate for the youth?",
  },
  SDInterpersonal: {
    details:
      "This item is used to identify a youth’s social and relationship skills. Interpersonal skills are rated independently of Social Functioning because a youth can have social skills but still struggle in his or her relationships at a particular point in time. This strength indicates an ability to make and maintain long-standing relationships.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Significant interpersonal strengths. Youth has well-developed interpersonal skills and healthy friendships.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth has good interpersonal skills and has shown the ability to develop healthy friendships.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth requires strength building to learn to develop good interpersonal skills and/or healthy friendships. Youth has some social skills that facilitate positive relationships with peers and adults but may not have any current healthy friendships.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. There is no evidence of observable interpersonal skills or healthy friendships at this time and/or youth requires significant help to learn to develop interpersonal skills and healthy friendships.  ",
      },
    ],
    considerations:
      "Does the youth have the trait ability to make friends? Do you feel that the youth is pleasant and likable? Do adults or same age peers like the youth?",
  },
  SDOptimism: {
    details:
      "This rating should be based on the youth’s sense of the youth’s self in their own future. This rates the youth’s future orientation.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth has a strong and stable optimistic outlook for his/her future.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth is generally optimistic about his/her future.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth has difficulty maintaining a positive view of him/herself and his/her life. Youth’s outlook may vary from overly optimistic to overly pessimistic.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. There is no evidence of optimism at this time and/or youth has difficulties seeing positive aspects about him/herself or his/her future.  ",
      },
    ],
    considerations:
      "Does the youth have a generally positive outlook on things; have things to look forward to? How does the youth see herself/himself in the future? Is the youth forward looking/sees herself/himself as likely to be successful?",
  },
  SDEducational: {
    details:
      "This item is used to evaluate the nature of the school’s relationship with the youth and family, as well as, the level of support the youth receives from the school. Rate according to how much the school is an effective partner in promoting youth’s functioning and addressing youth’s needs in school.",
    options: [
      {
        _id: "N",
        name: "N/A",
        description: "(N/A) = Youth is not in school  ",
      },
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. The school works closely with the youth and family to identify and successfully address the youth’s educational needs; OR the youth excels in school.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. School works with the youth and family to address the youth’s educational needs; OR the youth likes school.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. The school is currently unable to adequately address the youth’s academic or behavioral needs.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. There is no evidence of the school working to identify or successfully address the youth’s needs at this time and/or the school is unable and/or unwilling to work to identify and address the youth’s needs and/or there is no school to partner with at this time.  ",
      },
    ],
    considerations:
      "Is the school an active partner in the youth’s education? Does the youth like school? Has there been at least one year in which the youth did well in school? When has the youth been at their best in school?",
  },
  SDVocational: {
    details:
      "This item is used to refer to the strengths of the school/vocational environment and may or may not reflect any specific educational/work skills possessed by the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth is employed and is involved with a work environment that appears to exceed expectations. Job is consistent with developmentally appropriate career aspirations.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth is working; however, the job is not consistent with developmentally appropriate career aspirations. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth is temporarily unemployed. A history of consistent employment should be demonstrated and the potential for future employment without the need for vocational rehabilitation should be evidenced. This also may indicate a youth with a clear vocational preference. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. Youth is unemployed and has no clear vocational aspirations or a plan to achieve these aspirations. This level indicates a youth with no known or identifiable vocational skill and no expression of any future vocational preferences. ",
      },
    ],
    considerations:
      "Does the youth know what the youth wants to ‘be when they grows up?’ Has the youth ever worked or is the youth developing prevocational skills? Does the youth have plans to go to college or vocational school, for a career?",
  },
  SDTalents: {
    details:
      "This item refers to hobbies, skills, artistic interests, and talents that are positive ways that young people can spend their time, and also give them pleasure and a positive sense of self.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth has a talent that provides pleasure and/or self-esteem. Youth with significant creative/artistic/athletic strengths would be rated here. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth has a talent, interest, or hobby that has the potential to provide pleasure and self-esteem. This level indicates a youth with a notable talent. For example, a youth who is involved in athletics or plays a musical instrument would be rated here. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth has expressed interest in developing a specific talent, interest or hobby even if that talent has not been developed to date, or whether it would provide with any benefit. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. There is no evidence of identified talents, interests or hobbies at this time and/or youth requires significant assistance to identify and develop talents and interests. ",
      },
    ],
    considerations:
      "What does the youth do with free time? What does the youth enjoy doing? Is the youth engaged in any pro-social activities? What are the things that the youth does particularly well?",
  },
  SDReligious: {
    details:
      "This item refers to the youth’s experience of receiving comfort and support from religious or spiritual involvement. This item rates the presence of beliefs that could be useful to the youth; however, an absence of spiritual/religious beliefs does not represent a need for the family.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth is involved in and receives comfort and support from spiritual and/or religious beliefs, practices and/or community. Youth may be very involved in a religious community or may have strongly held spiritual or religious beliefs that can sustain or comfort the youth in difficult times. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth is involved in and receives some comfort and/or support from spiritual and/or religious beliefs, practices and/or community. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth has expressed some interest in spiritual or religious belief and practices. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. There is no evidence of identified spiritual or religious beliefs, nor does the youth show any interest in these pursuits at this time. ",
      },
    ],
    considerations:
      "Does the youth have spiritual beliefs that provide comfort? Is the family involved with any religious community? Is the youth involved? Is youth interested in exploring spirituality?",
  },
  SDCommunity: {
    details:
      "This item reflects the youth’s connection to people, places or institutions in their community. This connection is measured by the degree to which the youth is involved with institutions of that community which might include (but are not limited to) community centers, little league teams, jobs, after-school activities, religious groups, etc. Connections through specific people (e.g., friends and family) could be considered an important community connection, if many people who are important to the youth live in the same neighborhood.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth is well integrated into his/her community. The youth is a member of community organizations and has positive ties to the community. For example, individual may be a member of a community group (e.g. Girl or Boy Scout) for more than one year, may be widely accepted by neighbors, or involved in other community activities, informal networks, etc. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth is somewhat involved with his/her community. This level can also indicate a youth with significant community ties although they may be relatively short term. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth has an identified community but has only limited, or unhealthy, ties to that community. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. There is no evidence of an identified community of which youth is a member at this time. ",
      },
    ],
    considerations:
      "Does the youth feel like they are part of a community? Are there activities that the youth does in the community? Does the youth feel like they are part of a community? Are there activities that the youth does in the community?",
  },
  SDRelationships: {
    details:
      "This rating refers to the stability of significant relationships in the youth's life. This likely includes family members but may also include other individuals.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth who has very stable relationships. Family members, friends, and community have been stable for most of the youth’s life and are likely to remain so in the foreseeable future. Youth is involved with both parents. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth who has had stable relationships but there is some concern about instability in the near future (one year) due to transitions, illness, or age. A stable relationship with only one parent may be rated here. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth who has had at least one stable relationship over the youth’s lifetime but has experienced other instability through factors such as divorce, moving, removal from home, and death. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. Youth who does not have any stability in relationships. Independent living or adoption must be considered. ",
      },
    ],
    considerations:
      "Does the youth have relationships with adults that have lasted her/his lifetime? Is the youth in contact with both parents? Are there relatives in the youth’s life with whom the youth has long lasting relationships?",
  },
  SDResilience: {
    details:
      "This rating refers to the youth’s ability to recognize his or her internal strengths and use them in times of stress and in managing daily life. Resilience also refers to the youth’s ability to bounce back from stressful life events.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth’s internal strength in overcoming or the ability to bounce back is a core part of identity and associated with a well-developed and recognizable set of supports and strengths for dealing with challenges. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth uses internal strengths in overcoming or the ability to bounce back for healthy development, problem solving, or dealing with stressful life events. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth has limited ability to recognize and use internal strengths in overcoming or the ability to bounce back to effectively to support the youth’s healthy development, problem solving or dealing with stressful life events. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. Youth is currently unable to identify internal strengths for preventing or overcoming negative life events or outcomes. ",
      },
    ],
    considerations:
      "What does the youth do well? Is the youth able to recognize the youth’s skills as strengths? Is the youth able to use the youth’s strengths to problem solve and address difficulties or challenges?",
  },
  SDResourcefulness: {
    details:
      "This rating should be based on the youth’s ability to identify and use external/environmental strengths in managing daily life.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth is quite skilled at finding the necessary resources required to aid the youth in managing challenges. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth has some skills at finding necessary resources required to aid the youth in a healthy lifestyle but sometimes requires assistance at identifying or accessing these resources. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth has limited skills at finding necessary resources required to aid in achieving a healthy lifestyle and requires temporary assistance both with identifying and accessing these resources. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. Youth has no skills at finding the necessary resources to aid in achieving a healthy lifestyle and requires ongoing assistance with both identifying and accessing these resources. ",
      },
    ],
    considerations:
      "Does the youth have an external or environmental strengths? Does the youth use their external or environmental strengths to aid in their well being?",
  },
  SDIdentity: {
    details:
      "Cultural identify refers to the child’s view of self as belonging to a specific cultural group. This cultural group may be defined by a number of factors including race, religion, ethnicity, geography, sexual orientation or gender identity and expression (SOGIE).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. The youth has defined a cultural identity and is connected to others who support the youth’s cultural identity. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. The youth is developing a cultural identity and is seeking others to support the youth’s cultural identity. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. The youth is searching for a cultural identity and has not connected with others. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. The youth does not express a cultural identity. ",
      },
    ],
    considerations:
      "Does the youth identify with any racial/ ethnic/cultural group? Does the youth find this group a source of support?",
  },
  SDSupports: {
    details:
      "This item refers to unpaid helpers in the youth’s natural environment. These include individuals who provide social support to the target youth and family. All family members and paid caregivers are excluded.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Well-developed or centerpiece strength; may be used as a protective factor and a centerpiece of a strength-based plan. Youth has significant natural supports that contribute to helping support the youth’s healthy development. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Useful strength is evident but requires some effort to maximize the strength. Strength might be used and built upon in treatment. Youth has identified natural supports that provide some assistance in supporting the youth’s healthy development. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Strengths have been identified but require significant strength building efforts before they can be effectively utilized as part of a plan. Youth has some identified natural supports however the youth is not actively contributing to the youth’s healthy development. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = An area in which no current strength is identified; efforts are needed to identify potential strengths. Youth has no known natural supports (outside of family and paid caregivers). ",
      },
    ],
    considerations:
      "Who does the youth consider to be a support? Does the youth have non-family members in the youth’s life that are positive influences?",
  },
  CFDLanguage: {
    details:
      "This item looks at whether the youth and family need help with communication to obtain the necessary resources, supports and accommodations (e.g., interpreter). This item includes spoken, written, and sign language, as well as issues of literacy.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence that there is a need or preference for an interpreter and/or the youth and family speak and read the primary language where the youth or family lives. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth and/or family speak or read the primary language where the youth or family lives, but potential communication problems exist because of limited vocabulary or comprehension of the nuances of the language. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Youth and/or significant family members do not speak the primary language where the youth or family lives. Translator or family’s native language speaker is needed for successful intervention; a qualified individual(s) can be identified within natural supports. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth and/or significant family members do not speak the primary language where the youth or family lives. Translator or family’s native language speaker is needed for successful intervention; no such individual is available from among natural supports. ",
      },
    ],
    considerations:
      "What language does the family speak at home? Is there a youth interpreting for the family in situations that may compromise the youth or family’s care? Does the youth or significant family members have any special needs related to communication (e.g., ESL, ASL, Braille, or assisted technology)?",
  },
  CFDTraditions: {
    details:
      "This item rates the youth and family’s access to and participation in cultural tradition, rituals and practices, including the celebration of culturally specific holidays such as Kwanza, Dia de los Muertos, Yom Kippur, Quinceanera, etc. This also may include daily activities that are culturally specific (e.g., wearing a hijab, praying toward Mecca at specific times, eating a specific diet, access to media), and traditions and activities to include newer cultural identities.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth and/or family are consistently practice their chosen traditions and rituals consistent with their cultural identity. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth and/or family are generally practice their chosen traditions and rituals consistent with their cultural identity; however, they sometimes experience some obstacles to the performance of these practices. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Youth and/or family experience significant barriers and are sometimes prevented from practicing their chosen traditions and rituals consistent with their cultural identity. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth and/or family are unable to practice their chosen traditions and rituals consistent with their cultural identity. ",
      },
    ],
    considerations:
      "What holidays does the youth celebrate? What traditions are important to the youth? Does the youth fear discrimination for practicing the youth’s traditions and rituals?",
  },
  CFDStress: {
    details:
      "This item identifies circumstances in which the youth’s cultural identity is met with hostility or other problems within the youth’s environment due to differences in attitudes, behavior, or beliefs of others (this includes cultural differences that are causing stress between the youth and the youth’s family). Racism, negativity toward SOGIE and other forms of discrimination would be rated here.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of stress between the youth’s cultural identity and current living situation. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Some mild or occasional stress resulting from friction between the youth’s cultural identity and current living situation. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Youth is experiencing cultural stress that is causing problems of functioning in at least one life domain. Youth needs support to learn how to manage culture stress. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is experiencing a high level of cultural stress that is making functioning in any life domain difficult under the present circumstances. Youth needs immediate plan to reduce culture stress. ",
      },
    ],
    considerations:
      "What does the family believe is their reality of discrimination? How do they describe discrimination or oppression? Does this impact their functioning as both individuals and as a family? How does the caregiver support the youth’s identity and experiences if different from the youth‘s own?",
  },
  CNRDSupervision: {
    details:
      "This item rates the caregiver’s capacity to provide the level of monitoring and discipline needed by the youth. Discipline is defined in the broadest sense, and includes all of the things that parents/caregivers can do to promote positive behavior with their children.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence caregiver needs help or assistance in monitoring or disciplining the youth, and/or caregiver has good monitoring and discipline skills. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Caregiver generally provides adequate supervision, but is inconsistent. Caregiver may need occasional help or assistance. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver supervision and monitoring are very inconsistent and frequently absent. Caregiver needs assistance to improve supervision skills. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver is unable to monitor or discipline the youth. Caregiver requires immediate and continuing assistance. Youth is at risk of harm due to absence of supervision or monitoring. ",
      },
    ],
    considerations:
      "How does the caregiver feel about their ability to keep an eye on and discipline the youth? Does the caregiver need some help with these issues?",
  },
  CNRDInvolvement: {
    details:
      "This item rates the caregiver’s participation in the youth’s care and ability to advocate for the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of problems with caregiver involvement in services or interventions, and/or caregiver is able to act as an effective advocate for child. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Caregiver is consistently involved in the planning and/or implementation of services for the youth but is not an active advocate on behalf of the youth. Caregiver is open to receiving support, education, and information. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver is not actively involved in the youth’s services and/or interventions intended to assist. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver wishes for youth to be removed from his/her care. ",
      },
    ],
    considerations:
      "How involved are the caregivers in services for the youth? Is the caregiver an advocate for the youth? Would the caregiver like any help to become more involved?",
  },
  CNRDKnowledge: {
    details:
      "This item identifies the caregiver’s knowledge of the youth’s strengths and needs, and the youth’s ability to understand the rationale for the treatment or management of these problems.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of caregiver knowledge issues. Caregiver is fully knowledgeable about the youth's psychological strengths and weaknesses, talents and limitations. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Caregiver, while being generally knowledgeable about the youth, has some mild deficits in knowledge or understanding of the youth's psychological condition, talents, skills and assets. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver does not know or understand the youth well and significant deficits exist in the caregiver's ability to relate to the youth's problems and strengths. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver has little or no understanding of the youth's current condition. Caregiver’s lack of knowledge about the youth’s strengths and needs place the youth at risk of significant negative outcomes. ",
      },
    ],
    considerations:
      "Does the caregiver understand the youth’s current mental health diagnosis and/or symptoms? Does the caregiver’s expectations of the youth reflect an understanding of the youth’s mental or physical challenges?",
  },
  CNRDOrganization: {
    details:
      "This item is used to rate the caregiver’s ability to organize and manage their household within the context of intensive community services.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Caregiver is well organized and efficient. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Caregiver has minimal difficulties with organizing and maintaining household to support needed services. For example, may be forgetful about appointments or occasionally fails to return case manager calls. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver has moderate difficulty organizing and maintaining household to support needed services. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver is unable to organize household to support needed services. ",
      },
    ],
    considerations:
      "Do caregivers need or want help with managing their home? Do they have difficulty getting to appointments or managing a schedule? Do they have difficulty getting their youth to appointments or school?",
  },
  CNRDResources: {
    details:
      "This item rates the social assets (extended family) and resources that the caregiver can bring to bear in addressing the multiple needs of the youth and family.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Caregiver has significant social and family networks that actively help with caregiving. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Caregiver has some family or friend or social network that actively helps with caregiving. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Work needs to be done to engage family, friends or social network in helping with caregiving. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver has no family or social network to help with caregiving. ",
      },
    ],
    considerations:
      "Does family have extended family or friends who provide emotional support? Can they call on social supports to watch the youth occasionally?",
  },
  CNRDStability: {
    details:
      "This item rates the housing stability of the caregiver(s) and does not include the likelihood that the youth or youth will be removed from the household.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Caregiver has stable housing with no known risks of instability ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Caregiver has relatively stable housing but either has moved in the recent past or there are indications of housing problems that might force housing disruption. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver has moved multiple times in the past year. Housing is unstable. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action Family is homeless, or has experienced homelessness in the recent past. ",
      },
    ],
    considerations:
      "Is the family’s current housing situation stable? Are there concerns that they might have to move in the near future? Has family lost their housing?",
  },
  CNRDMedical: {
    details:
      "This item refers to medical and/or physical problems that the caregiver(s) may be experiencing that prevent or limit his or her ability to provide care for the youth. This item does not rate depression or other mental health issues.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention No evidence of medical or physical health problems. Caregiver is generally healthy. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history or suspicion of, and/or caregiver is in recovery from medical/physical problems. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver has medical/physical problems that interfere with the capacity to parent the youth. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver has medical/physical problems that make parenting the youth impossible at this time. ",
      },
    ],
    considerations:
      "How is the caregiver’s health? Does the caregiver have any health problems that limit their ability to care for the family?",
  },
  CNRDMental: {
    details:
      "This item refers to any serious mental health issues (not including substance abuse) among caregivers that might limit their capacity to provide care for the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of caregiver mental health difficulties. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history or suspicion of mental health difficulties, and/or caregiver is in recovery from mental health difficulties. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver’s mental health difficulties interfere with his or her capacity to parent. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver has mental health difficulties that make it impossible to parent the youth at this time. ",
      },
    ],
    considerations:
      "Do caregivers have any mental health needs (including adjusting to trauma experiences) that make parenting difficult? Is the youth receiving services? Is there any evidence of transgenerational trauma that is impacting the caregiver or the youth’s ability to give care effectively?",
  },
  CNRDSubstances: {
    details:
      "This item rates the impact of any notable substance use by caregivers that might limit their capacity to provide care for the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of caregiver substance use issues. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history of, suspicion or mild use of substances and/or caregiver is in recovery from substance use difficulties where there is no interference in his/her ability to parent. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver has some substance abuse difficulties that interfere with his or her capacity to parent. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver has substance abuse difficulties that make it impossible to parent the youth at this time. ",
      },
    ],
    considerations:
      "Do caregivers have any substance use needs that make parenting difficult? Is the caregiver receiving any services for the substance use problems?",
  },
  CNRDDevelopmental: {
    details:
      "This item describes the presence of limited cognitive capacity or developmental disabilities that challenges the caregiver’s ability to provide care for the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of caregiver developmental disabilities or challenges. Caregiver has no developmental needs. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Caregiver has developmental challenges. The developmental challenges do not currently interfere with parenting. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Caregiver has developmental challenges that interfere with the capacity to parent the youth. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Caregiver has severe developmental challenges that make it impossible to parent the youth at this time. ",
      },
    ],
    considerations:
      "Does the caregiver have developmental challenges that make parenting/caring for the youth difficult? Does the caregiver have services?",
  },
  CNRDSafety: {
    details:
      "This item describes the caregiver’s ability to maintain the youth’s safety within the household. It does not refer to the safety of other family or household members based on any danger presented by the assessed caregiver.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of safety issues. Household is safe and secure. Youth is not at risk from others. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Household is safe but concerns exist about the safety of the youth due to history or others who might be abusive. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with functioning. Youth is in some danger from one or more individuals with access to the home. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth is in immediate danger from one or more individuals with unsupervised access. ",
      },
    ],
    considerations:
      "Is the caregiver able to protect the youth from harm in the home? Are there individuals living in the home or visiting the home that may be abusive to the youth?",
  },
  BENDPsychosis: {
    details:
      "This item rates the symptoms of psychiatric disorders with a known neurological base, including schizophrenia spectrum and other psychotic disorders. The common symptoms of these disorders include hallucinations (i.e. experiencing things others do not experience), delusions (i.e. a false belief or an incorrect inference about reality that is firmly sustained despite the fact that nearly everybody thinks the belief is false or proof exists of its inaccuracy), disorganized thinking, and bizarre/idiosyncratic behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of psychotic symptoms. Both thought processes and content are within normal range. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Evidence of disruption in thought processes or content. Youth may be somewhat tangential in speech or evidence somewhat illogical thinking (age-inappropriate). This also includes youth with a history of hallucinations but none currently. Use this category for youth who are below the threshold for one of the DSM diagnoses listed above. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Evidence of disturbance in thought process or content that may be impairing the youth’s functioning in at least one life domain. Youth may be somewhat delusional or have brief intermittent hallucinations. Speech may be at times quite tangential or illogical. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Clear evidence of dangerous hallucinations, delusions, or bizarre behavior that might be associated with some form of psychotic disorder that places the youth or others at risk of physical harm. ",
      },
    ],
    considerations:
      "Does the youth exhibit behaviors that are unusual or difficult to understand? Does the youth engage in certain actions repeatedly? Are the unusual behaviors or repeated actions interfering with the youth’s functioning?",
  },
  BENDImpulsivity: {
    details:
      "Problems with impulse control and impulsive behaviors, including motoric disruptions, are rated here. This includes behavioral symptoms associated with Attention-Deficit Hyperactivity Disorder (ADHD), Impulse-Control Disorders and mania as indicated in the DSM-5. Children with impulse problems tend to engage in behavior without thinking, regardless of the consequences. This can include compulsions to engage in gambling, violent behavior (e.g., road rage), and sexual behavior, fire-starting or stealing. Manic behavior is also rated here.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of symptoms of loss of control of behavior. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history or evidence of mild levels of impulsivity evident in action or thought that place the youth at risk of future functioning difficulties. The youth may exhibit limited impulse control, e.g., youth may yell out answers to questions or may have difficulty waiting one’s turn. Some motor difficulties may be present as well, such as pushing or shoving others. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Clear evidence of problems with impulsive, distractible, or hyperactive behavior that interferes with the youth’s functioning in at least one life domain. This indicates a youth with impulsive behavior who may represent a significant management problem for adults (e.g., caregivers, teachers, coaches, etc.). A youth who often intrudes on others and often exhibits aggressive impulses would be rated here. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Clear evidence of a dangerous level of hyperactivity and/or impulsive behavior that places the youth at risk of physical harm. This indicates a youth with frequent and significant levels of impulsive behavior that carries considerable safety risk (e.g., running into the street, dangerous driving or bike riding). The youth may be impulsive on a nearly continuous basis. The youth endangers self or others without thinking. ",
      },
    ],
    considerations:
      "Is the youth unable to sit still for any length of time? Does the youth have trouble paying attention for more than a few minutes? Is the youth able to control the youth’s behavior, talking?",
  },
  BENDDepression: {
    details:
      "Symptoms included in this item are irritable or depressed mood, social withdrawal, sleep disturbances, weight/eating disturbances, and loss of motivation, interest or pleasure in daily activities. This item can be used to rate symptoms of the depressive disorders as specified in DSM-5.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of problems with depression. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. History or suspicion of depression or evidence of depression associated with a recent negative life event with minimal impact on life domain functioning. Brief duration of depression, irritability, or impairment of peer, family, or academic functioning that does not lead to pervasive avoidance behavior. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Clear evidence of depression associated with either depressed mood or significant irritability. Depression has interfered significantly in youth’s ability to function in at least one life domain. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Clear evidence of disabling level of depression that makes it virtually impossible for the youth to function in any life domain. This rating is given to a youth with a severe level of depression. This would include a youth who stays at home or in bed all day due to depression or one whose emotional symptoms prevent any participation in school, friendship groups, or family life. Disabling forms of depressive diagnoses would be rated here. ",
      },
    ],
    considerations:
      "Is youth concerned about possible depression or chronic low mood and irritability? Has the youth withdrawn from normal activities? Does the youth seem lonely or not interested in others?",
  },
  BENDAnxiety: {
    details:
      "This item rates symptoms associated with DSM-5 Anxiety Disorders characterized by excessive fear and anxiety and related behavioral disturbances (including avoidance behaviors). Panic attacks can be a prominent type of fear response.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of anxiety symptoms. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history, suspicion, or evidence of mild anxiety associated with a recent negative life event. This level is used to rate either a mild phobia or anxiety problem that is not yet causing the individual significant distress or markedly impairing functioning in any important context. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Clear evidence of anxiety associated with either anxious mood or significant fearfulness. Anxiety has interfered in the youth’s ability to function in at least one life domain. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Clear evidence of debilitating level of anxiety that makes it virtually impossible for the youth to function in any life domain. ",
      },
    ],
    considerations:
      "Does the youth have any problems with anxiety or fearfulness? Is the youth avoiding normal activities out of fear? Does the youth act frightened or afraid?",
  },
  BENDOppositional: {
    details:
      "This item rates the youth’s relationship with authority figures. Generally oppositional behavior is displayed in response to conditions set by a parent, teacher or other authority figure with responsibility for and control over the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of oppositional behaviors. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history or evidence of mild level of defiance towards authority figures that has not yet begun to cause functional impairment. Youth may occasionally talk back to teacher, parent/caregiver; there may be letters or calls from school. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Clear evidence of oppositional and/or defiant behavior towards authority figures that is currently interfering with the youth’s functioning in at least one life domain. Behavior causes emotional harm to others. A youth whose behavior meets the criteria for Oppositional Defiant Disorder in DSM-5 would be rated here. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Clear evidence of a dangerous level of oppositional behavior involving the threat of physical harm to others. This rating indicates that the youth has severe problems with compliance with rules or adult instruction or authority. ",
      },
    ],
    considerations:
      "Does the youth follow their caregivers’ rules? Have teachers or other adults reported that the youth does not follow rules or directions? Does the youth argue with adults when they try to get the youth to do something? Does the youth do things that they have been explicitly told not to do?",
  },
  BENDConduct: {
    details:
      "This item rates the degree to which a youth engages in behavior that is consistent with the presence of a Conduct Disorder.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of serious violations of others or laws. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. There is a history, suspicion or evidence of some problems associated with antisocial behavior including but not limited to lying, stealing, manipulation of others, acts of sexual aggression, or violence towards people, property or animals. The youth may have some difficulties in school and home behavior. Problems are recognizable but not notably deviant for age, sex and community. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Clear evidence of antisocial behavior including but not limited to lying, stealing, manipulating others, sexual aggression, violence towards people, property, or animals. A youth rated at this level will likely meet criteria for a diagnosis of Conduct Disorder. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Evidence of a severe level of aggressive or antisocial behavior, as described above, that places the youth or community at significant risk of physical harm due to these behaviors. This could include frequent episodes of unprovoked, planned aggressive or other antisocial behavior. ",
      },
    ],
    considerations:
      "Is the youth seen as dishonest? How does the youth handle telling the truth/lies? Has the youth been part of any criminal behavior? Has the youth ever shown violent or threatening behavior towards others? Has the youth ever tortured animals? Does the youth disregard or is unconcerned about the feelings of others (lack empathy)?",
  },
  BENDAdjustment: {
    details:
      "This item is used to describe the youth who is having difficulties adjusting to a traumatic experience, as defined by the youth. This is one item where speculation about why a person is displaying a certain behavior is considered. There should be an inferred link between the trauma and behavior.*A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the Trauma Module (pg. 50).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence that youth has experienced a traumatic life event, OR youth has adjusted well to traumatic/adverse experiences. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. The youth has experienced a traumatic event and there are some changes in his/her behavior that are managed or supported by caregivers. These symptoms are expected to ease with the passage of time and therefore no current intervention is warranted. Youth may be in the process of recovering from a more extreme reaction to a traumatic experience, which may require a need to watch these symptoms or engage in preventive action. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Clear evidence of adjustment problems associated with traumatic life event(s). Symptoms can vary widely and may include sleeping or eating disturbances, regressive behavior, behavior problems or problems with attachment. Adjustment is interfering with youth’s functioning in at least one life domain. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Clear evidence of debilitating level of trauma symptoms that makes it virtually impossible for the youth to function in any life domain including symptoms such as flashbacks, nightmares, significant anxiety, intrusive thoughts, and/or re-experiencing trauma (consistent with PTSD). ",
      },
    ],
    considerations:
      "What was the youth’s trauma? How is it connected to the current issue(s)? What are the youth’s coping skills? Who is supporting the youth?",
  },
  BENDAttachment: {
    details:
      "This item rates the level of difficulties the youth has with attachment and their ability to form relationships.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence of attachment problems. Caregiver-youth relationship is characterized by mutual satisfaction of needs and youth's development of a sense of security and trust. Caregiver is able to respond to youth cues in a consistent, appropriate manner, and youth seeks age-appropriate contact with caregiver for both nurturing and safety needs. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Some history or evidence of insecurity in the caregiver-youth relationship. Caregiver may have difficulty accurately reading youth’s bids for attention and nurturance; may be inconsistent in response; or may be occasionally intrusive. Youth may have some problems with separation (e.g., anxious/clingy behaviors in the absence of obvious cues of danger) or may avoid contact with caregiver in age-inappropriate way. Youth may have minor difficulties with appropriate physical/emotional boundaries with others. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Problems with attachment that interfere with youth’s functioning in at least one life domain and require intervention. Caregiver may consistently misinterpret youth cues, act in an overly intrusive way, or ignore/avoid youth bids for attention/nurturance. Youth may have ongoing difficulties with separation, may consistently avoid contact with caregivers, and have ongoing difficulties with physical or emotional boundaries with others. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth is unable to form attachment relationships with others (e.g., chronic dismissive/avoidant/detached behavior in care giving relationships) OR youth presents with diffuse emotional/physical boundaries leading to indiscriminate attachment with others. Youth is considered at ongoing risk due to the nature of his/her attachment behaviors. Youth may have experienced significant early separation from or loss of caregiver, or have experienced chronic inadequate care from early caregivers, or youth may have individual vulnerabilities (e.g., mental health, developmental disabilities) that interfere with the formation of positive attachment relationships. ",
      },
    ],
    considerations:
      "Does the youth struggle with separating from caregiver? Does the youth approach or attach to strangers?",
  },
  BENDAnger: {
    details:
      "This item captures the youth’s ability to identify and manage the youth’s anger when frustrated.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of any anger control problems. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. History, suspicion of, or evidence of some problems with controlling anger. Youth may sometimes become verbally aggressive when frustrated. Peers and family are aware of and may attempt to avoid stimulating angry outbursts. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth’s difficulties with controlling anger are impacting functioning in at least one life domain. Youth’s temper has resulted in significant trouble with peers, family and/or school. Anger may be associated with physical violence. Others are likely quite aware of anger potential. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth’s temper or anger control problem is dangerous. Youth frequently gets into fights that are often physical. Others likely fear the youth. ",
      },
    ],
    considerations:
      "How does the youth control their emotions? Does the youth get upset or frustrated easily? Does the youth overreact if someone criticizes or rejects the youth? Does the youth seem to have dramatic mood swings?",
  },
  BENDSubstances: {
    details:
      "This item describes problems related to the use of alcohol and illegal drugs, the misuse of prescription medications, and the inhalation of any chemical or synthetic substance by a youth. This rating is consistent with DSM-5 Substance-Related and Addictive Disorders. This item does not apply to the use of tobacco or caffeine.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth has no notable substance use difficulties at the present time. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Identified need requires monitoring, watchful waiting, or preventive activities. Youth has substance use problems that occasionally interfere with daily life (e.g., intoxication, loss of money, reduced work/school performance, parental concern). History of substance use problems without evidence of current problems related to use is rated here. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed; need is interfering with youth’s functioning. Youth has a substance use problem that consistently interferes with the ability to function optimally, but does not completely preclude functioning in an unstructured setting. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Problems are dangerous or disabling; requires immediate and/or intensive action. Youth has a substance use problem that represents complications to functional issues that may result in danger to self, public safety issues, or the need for detoxification of the youth. ",
      },
    ],
    considerations:
      "Has the youth used alcohol or drugs on more than an experimental basis? Do you suspect that the youth may have an alcohol or drug use problem? Has the youth been in a recovery program for the use of alcohol or illegal drugs?",
  },
  YRBDSuicide: {
    details:
      "This item is intended to describe the presence of thoughts or behaviors aimed at taking one’s life. This rating describes both suicidal and significant self-injurious behavior. This item rates overt and covert thoughts and efforts on the part of a youth or youth to end his/ her life. A rating of ‘2’ or ‘3’ would indicate the need for a safety plan. Notice the specific time frames for each rating.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence of suicidal ideation. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History of suicidal ideation, but no recent ideation or gesture. History of suicidal behaviors or significant ideation but none during the recent past. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Recent ideation or gesture. Recent, but not acute, suicidal ideation or gesture ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Current ideation and intent OR command hallucinations that involve self-harm. Current suicidal ideation and intent. ",
      },
    ],
    considerations:
      "Has the youth ever talked about a wish or plan to die or to kill the youth’s self? Has the youth ever tried to commit suicide?",
  },
  YRBDInjurious: {
    details:
      "This rating includes repetitive, physically harmful behavior that generally serves as a self-soothing function to the youth (e.g., cutting, carving, burning self, face slapping, head banging, etc.).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence of any forms of self-injury. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. A history or suspicion of self-injurious behavior. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Engaged in self-injurious behavior (cutting, burns, piercing skin with sharp objects, repeated head banging) that does not require medical attention. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Engaged in self-injurious behavior requiring medical intervention (e.g., sutures, surgery) and that is significant enough to put the youth’s health at risk. ",
      },
    ],
    considerations:
      "Does the behavior serve a self-soothing purpose (e.g., numb emotional pain, move the focus of emotional pain to the physical)? Does the youth ever purposely hurt oneself (e.g., cutting)?",
  },
  YRBDRecklessness: {
    details:
      "This rating includes reckless and dangerous behaviors that, while not intended to harm self or others, place the youth or others in some jeopardy. Suicidal or self-injurious behaviors are not rated here.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence of behaviors (other than suicide or self-mutilation) that place the youth at risk of physical harm. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. There is a history, suspicion or mild behavior (other than suicide or self-mutilation) that places youth at risk of physical harm such as reckless and dangerous risk-taking behavior. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Engaged in reckless or intentional risk-taking behavior (other than suicide or self-mutilation) that places the youth in danger of physical harm. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Engaged in reckless or intentional risk-taking behavior (other than suicide or self-mutilation) that places the youth at immediate risk of death. ",
      },
    ],
    considerations:
      "Does the youth act without thinking? Has the youth ever talked about or acted in a way that might be dangerous to the youth’s self? (e.g., reckless behavior such as riding on top of cars, reckless driving, climbing bridges, etc.)?",
  },
  YRBDDanger: {
    details:
      "This item rates the youth’s violent or aggressive behavior. The intention of this behavior is to cause significant bodily harm to others. A rating of ‘2’ or ‘3’ would indicate the need for a safety plan. Reckless behavior that may cause physical harm to others is not rated on this item.*A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the Violence Module (pg. 62).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence or history of aggressive behaviors or significant verbal threats of aggression towards others (including people and animals). ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History of aggressive behavior or verbal threats of aggression towards others. History of fire setting would be rated here. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Occasional or moderate level of aggression towards others. Youth has made verbal threats of violence towards others. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Acute homicidal ideation with a plan, frequent or dangerous (significant harm) level of aggression to others. Youth is an immediate risk to others. ",
      },
    ],
    considerations:
      "Has the youth ever injured another person on purpose? Does the youth get into physical fights? Has the youth ever threatened to kill or seriously injure others?",
  },
  YRBDSexual: {
    details:
      "This item is intended to describe both aggressive sexual behavior and sexual behavior in which the youth takes advantage of a younger or less powerful youth. The severity and recency of the behavior provide the information needed to rate this item.*A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the Sexually Aggressive Behavior Module (pg. 67).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence of sexually aggressive behavior. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History or suspicion of sexually aggressive behavior and/or sexually inappropriate behavior within the past year that troubles others such as harassing talk or public masturbation. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth engages in sexually aggressive behavior that negatively impacts functioning. For example, frequent inappropriate sexual behavior (e.g., inappropriate touching of others). Frequent disrobing would be rated here only if it was sexually provocative. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth engages in a dangerous level of sexually aggressive behavior. This would indicate the rape or sexual abuse of another person involving sexual penetration. ",
      },
    ],
    considerations:
      "Has the youth ever been accused of being sexually aggressive towards another youth? Has the youth had sexual contact with a younger individual?",
  },
  YRBDRunaway: {
    details:
      "This item describes the risk of running away or actual runaway behavior.*A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the Runaway Module (pg. 70).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. Youth has no history of running away or ideation of escaping from current living situation. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Youth has no recent history of running away but has not expressed ideation about escaping current living situation. Youth may have threatened running away on one or more occasions or has a history of running away but not in the recent past. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth has run from home once or run from one treatment setting. Also rated here is a youth who has runaway to home (parental or relative). ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth has run from home and/or treatment settings in the recent past and present an imminent flight risk. A youth who is currently a runaway is rated here. ",
      },
    ],
    considerations:
      "Has the youth ever run away from home, school, or any other place? If so, where did the youth go? How long did the youth stay away? How was the youth found? Does the youth ever threaten to run away?",
  },
  YRBDDelinquent: {
    details:
      "This rating includes both criminal behavior and status offenses that may result from youth failing to follow required behavioral standards (e.g., truancy, curfew violations, driving without a license). Sexual offenses should be included as criminal behavior. If caught, the youth could be arrested for this behavior.*A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the Juvenile Justice Module (page 72).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence or no history of delinquent behavior. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History or suspicion of delinquent behavior, but none in the recent past. Status offenses would generally be rated here. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Currently engaged in delinquent behavior (e.g., vandalism, shoplifting, etc.) that puts the youth at risk. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Serious recent acts of delinquent activity that place others at risk of significant loss or injury, or place the youth at risk of adult sanctions. Examples include car theft, residential burglary and gang involvement. ",
      },
    ],
    considerations:
      "Do you know of laws that the youth has broken (even if the youth has not been charged or caught)? Has the youth ever been arrested?",
  },
  YRBDArson: {
    details:
      "This item refers to behavior involving the intentional setting of fires that might be dangerous to the youth or others. This includes both malicious and non-malicious fire-setting. This does NOT include the use of candles or incense or matches to smoke or accidental fire-setting.*A rating of ‘1’, ‘2’ or ‘3’ on this item triggers the Fire Setting Module (pg. 74).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. No evidence of fire setting by the youth. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History of fire setting but not in the recent past. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Recent fire setting behavior but not of the type that has endangered the lives of others OR repeated fire-setting behavior in the recent past. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Acute threat of fire setting. Set fire that endangered the lives of others (e.g. attempting to burn down a house). ",
      },
    ],
    considerations:
      "Has the youth ever started a fire? Has the incident of fire setting put anyone at harm or at risk of harm?",
  },
  YRBDMisbehavior: {
    details:
      "This rating describes intentional behaviors that a youth engages in to force others to administer consequences. This item should reflect problematic social behaviors (socially unacceptable behavior for the culture and community in which the youth lives) that put the youth at some risk of consequences. It is not necessary that the youth be able to articulate that the purpose of his/her misbehavior is to provide reactions/consequences to rate this item. There is always, however, a benefit to the youth resulting from this unacceptable behavior even if it does not appear this way on the face of it (e.g., youth feels more protected, more in control, less anxious because of the sanctions). This item should not be rated for youth who engage in such behavior solely due to developmental delays.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. Youth shows no evidence of problematic social behaviors that cause adults to administer consequences. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Some problematic social behaviors that force adults to administer consequences to the youth. Provocative comments or behavior in social settings aimed at getting a negative response from adults might be included at this level. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth may be intentionally getting in trouble in school or at home and the consequences, or threat of consequences is causing problems in the youth’s life. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Frequent seriously inappropriate social behaviors force adults to seriously and/or repeatedly administer consequences to the youth. The inappropriate social behaviors may cause harm to others and/or place the youth at risk of significant consequences (e.g. expulsion from school, removal from the community). ",
      },
    ],
    considerations:
      "Does the youth intentionally do or say things to upset others or get in trouble with people in positions of authority or (e.g., parents or teachers)? Has the youth engaged in behavior that was insulting, rude or obnoxious and which resulted in sanctions for the youth such as suspension, job dismissal, etc.?",
  },
  DNMCognitive: {
    details: "This item rates the youth’s IQ and cognitive functioning.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. Youth shows no evidence of problematic social behaviors that cause adults to administer consequences. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Some problematic social behaviors that force adults to administer consequences to the youth. Provocative comments or behavior in social settings aimed at getting a negative response from adults might be included at this level. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth may be intentionally getting in trouble in school or at home and the consequences, or threat of consequences is causing problems in the youth’s life. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Frequent seriously inappropriate social behaviors force adults to seriously and/or repeatedly administer consequences to the youth. The inappropriate social behaviors may cause harm to others and/or place the youth at risk of significant consequences (e.g. expulsion from school, removal from the community). ",
      },
    ],
    considerations:
      "Has the youth been tested for or diagnosed with a learning disability? Does the youth have an intellectual disability or delay?",
  },
  DNMCommunication: {
    details:
      "This item rates the youth’s ability to communicate with others via expression and reception.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth's receptive and expressive communication appears developmentally appropriate. There is no reason to believe that the youth has any problems communicating. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has receptive communication skills but limited expressive communication skills. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has both limited receptive and expressive communication skills. ",
      },
      {
        _id: "3",
        name: "3",
        description: "(3) = Youth is unable to communicate. ",
      },
    ],
    considerations:
      "Is the youth vocal about their needs and wants? Has the youth ever been diagnosed with a communication disorder?",
  },
  DNMDevelopmental: {
    details:
      "This item rates the level of developmental delay/disorders that are present.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth's development appears within normal range. There is no reason to believe that the youth has any developmental problems. ",
      },
      {
        _id: "1",
        name: "1",
        description: "(1) = Evidence of a mild developmental delay. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Evidence of a pervasive developmental disorder including Autism, Tourette's, Down's Syndrome or other significant developmental delay. ",
      },
      {
        _id: "3",
        name: "3",
        description: "(3) = Severe developmental disorder. ",
      },
    ],
    considerations:
      "Is the youth progressing developmentally in a way similar to peers of the same age? Has the youth been diagnosed with a developmental disorder?",
  },
  DNMSkills: {
    details:
      "This item aims to describe the youth’s ability and motivation to engage in developmentally appropriate self-care tasks such as eating, bathing, dressing, toileting, and other such tasks related to keeping up with one’s personal hygiene.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth's self-care and daily living skills appear developmentally appropriate. There is no reason to believe that the youth has any problems performing daily living skills. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth requires verbal prompting on self-care tasks or daily living skills. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth requires assistance (physical prompting) on self-care tasks or attendant care on one self care task (e.g. eating, bathing, dressing, and toileting). ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth requires attendant care on more than one of the self-care tasks-eating, bathing, dressing, toileting. ",
      },
    ],
    considerations:
      "Does the youth show age appropriate self-care skills? Is the youth able to groom themselves?",
  },
  TMSexual: {
    details: "This item rates the severity and frequency of sexual abuse.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has experienced sexual abuse. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has experienced sexual abuse, or there is a suspicion that he/she has experienced sexual abuse – single or multiple episodes, or chronic over an extended period of time. The abuse may have involved penetration, multiple perpetrators, and/or associated physical injury. Youth with exposure to secondary sexual abuse (e.g., witnessing sexual abuse, having a sibling sexually abused) should be rated here. ",
      },
    ],
    considerations:
      "Has the caregiver or youth disclosed sexual abuse? How often did the abuse occur? Did the abuse result in physical injury?",
  },
  TMPhysical: {
    details:
      "This item rates the severity and frequency of experiences of physical abuse.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has experienced physical abuse. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has experienced or there is a suspicion that he/she has experienced physical abuse – mild to severe, or repeated physical abuse with sufficient physical harm requiring medical treatment. ",
      },
    ],
    considerations:
      "Is physical discipline used in the home? What forms? Has the youth ever received bruises, marks, or injury from discipline?",
  },
  TMNeglect: {
    details:
      "This rating describes whether or not the youth has experienced neglect. Neglect can refer to a lack of food, shelter or supervision (physical neglect), lack of access to needed medical care (medical neglect), or failure to receive academic instruction (educational neglect).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has experienced neglect. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has experienced neglect, or there is a suspicion that he/she has experienced neglect. This includes occasional neglect (e.g., youth left home alone for a short period of time when developmentally inappropriate and with no adult supervision, or occasional failure to provide adequate supervision of the youth); multiple and/or prolonged absences of adults, with minimal supervision; or failure to provide basic necessities of life (adequate food, shelter, or clothing) on a regular basis. ",
      },
    ],
    considerations:
      "Is the youth receiving adequate supervision? Are the youth’s basic needs for food and shelter being met? Is the youth allowed access to necessary medical care? Education?",
  },
  TMEmotional: {
    details:
      "This item rates whether the youth has experienced verbal and nonverbal emotional abuse, including belittling, shaming, and humiliating a child, calling names, making negative comparisons to others, or telling a child that he or she is, “no good.” This item includes both “emotional abuse,” which would include psychological maltreatment such as insults or humiliation towards a child and “emotional neglect,” described as the denial of emotional attention and/or support from caregivers.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that youth has experienced emotional abuse. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has experienced emotional abuse, or there is a suspicion that he/she has experienced emotional abuse (mild to severe, for any length of time) including: insults or occasionally being referred to in a derogatory manner by caregivers, being denied emotional attention or completely ignored, or threatened/terrorized by others. ",
      },
    ],
    considerations:
      "How does the caregiver talk to/interact with the youth? Is there name calling or shaming in the home?",
  },
  TMMedical: {
    details:
      "This item rates the youth’s experience of medically related trauma, including inpatient hospitalizations, outpatient procedures, and significant injuries.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has experienced any medical trauma. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has had a medical experience that was perceived as emotionally or mentally overwhelming. This includes events that were acute in nature and did not result in ongoing medical needs; associated distress such as minor surgery, stitches or bone setting; acute injuries and moderately invasive medical procedures such as major surgery that required only short term hospitalization; events that may have been life threatening and may have resulted in chronic health problems that alter the youth’s physical functioning. A suspicion that a youth has had a medical experience that was perceived as emotionally or mentally overwhelming should be rated here. ",
      },
    ],
    considerations:
      "Has the youth had any broken bones, stitches or other medical procedures? Has the youth had to go to the emergency room, or stay overnight in the hospital?",
    supplemental:
      "This item takes into account the impact of the event on the youth. It describes experiences in which the youth is subjected to medical procedures that are experienced as upsetting and overwhelming. A youth born with physical deformities who is subjected to multiple surgeries could be included. A youth who must experience chemotherapy or radiation could also be included. Youth who experience an accident and require immediate medical intervention that results in on-going physical limitations or deformities (e.g., burn victims) could be in included here. Common medical procedures, which are generally not welcome or pleasant but are also not emotionally or psychologically overwhelming for children (e.g., shots, pills) would generally not be rated here.",
  },
  TMDisaster: {
    details:
      "This rating describes the youth’s exposure to either natural or manmade disasters.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has experienced, been exposed to or witnessed natural or man-made disasters. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has experienced, been exposed to or witnessed natural or manmade disasters either directly or second-hand (i.e. on television, hearing others discuss disasters). This includes disasters such as a fire or earthquake or manmade disaster; car accident, plane crashes, or bombings; observing a caregiver who has been injured in a car accident or fire or watching a neighbor’s house burn down; a disaster that caused significant harm or death to a loved one; or there is an ongoing impact or life disruption due to the disaster (e.g. caregiver loses job). A suspicion that the youth has experienced, been exposed to or witnessed natural or manmade disasters either directly or second-hand (i.e. on television, hearing others discuss disasters) would be rated here. ",
      },
    ],
    considerations:
      "Has the youth been present during a natural or man-made disaster? Does the youth watch television shows containing these themes or overhear adults talking about these kinds of disasters?",
  },
  TMFamily: {
    details: "This item rates the violence within the youth’s home or family",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence the youth has witnessed family violence. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has witnessed, or there is a suspicion that he/she has witnessed family violence – single, repeated, or severe episodes. This includes episodes of family violence but no significant injuries (i.e. requiring emergency medical attention) and episodes in which significant injuries have occurred as a direct result of the violence. ",
      },
    ],
    considerations:
      "Is there frequent fighting in the youth’s family? Does the fighting ever become physical?",
  },
  TMCommunity: {
    details:
      "This item rates the severity and frequency of incidents of violence the youth has witnessed in his/her community. This includes witnessing violence at the youth’s school or educational setting.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has witnessed violence in the community or his/her school. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has witnessed or experienced violence in the community or his/her school, such as: fighting; friends/family injuries as a result of violence; severe and repeated instances of violence and/or the death of another person in his/her community/school as a result of violence; is the direct victim of violence/criminal activity in the community/school that was life threatening; or has experienced chronic/ongoing impact as a result of community/school violence (e.g., family member injured and no longer able to work). A suspicion that the youth has witnessed or experienced violence in the community would be rated here. ",
      },
    ],
    considerations:
      "Does the youth live in a neighborhood with frequent violence? Has the youth witnessed or directly experienced violence at his/her school?",
  },
  TMCriminal: {
    details:
      "This rating describes the severity of exposure to criminal activity. Criminal behavior includes any behavior for which an adult could go to prison including drug dealing, prostitution, assault, or battery.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has been victim or a witness to criminal activity. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has been victimized, or there is suspicion that he/she has been victimized or has witnessed criminal activity. This includes a single instance, multiple instances, or chronic and severe instances of criminal activity that was life threatening or caused significant physical harm, or youth has witnessed the death of a family friend, loved one. ",
      },
    ],
    considerations:
      "Has the youth or someone in his/her family ever been the victim of a crime? Has the youth seen criminal activity in the community or home?",
    supplemental:
      "Any behavior that could result in incarceration is considered criminal activity. A youth who has been sexually abused or witnesses a sibling being sexually abused or physically abused to the extent that assault charged could be filed would be rated here and on the appropriate abuse-specific items. A youth who has witnessed drug dealing, prostitution, assault or battery would also be rated on this item.",
  },
  TMWarAffected: {
    details:
      "This rating describes the degree of severity of exposure to war, political violence, torture or terrorism.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = No evidence that the youth has been exposed to war, political violence, torture or terrorism. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has experienced, or there is suspicion that he/she has experienced or been affected by war, terrorism or political violence. Examples include: Family members directly related to the youth may have been exposed to war, political violence, or torture resulting in displacement, injury or disability, or death; parents may have been physically or psychologically disabled from the war and are unable to adequately care for the youth; youth may have spent an extended amount of time in a refugee camp, or feared for his/her own life during war or terrorism due to bombings or shelling very near to him/her; youth may have been directly injured, tortured, or kidnapped in a terrorist attack; youth may have served as a soldier, guerrilla, or other combatant in his/her home country. Youth who did not live in war or terrorism-affected region or refugee camp, but family was affected by war. ",
      },
    ],
    considerations:
      "Has the youth or his/her family lived in a war torn region? How close was she/he to war or political violence, torture or terrorism? Was the family displaced?",
    supplemental:
      "Terrorism is defined as “the calculated use of violence or the threat of violence to inculcate fear, intended to coerce or to intimidate governments or societies in the pursuit of goals that are generally political, religious or ideological.” Terrorism includes attacks by individuals acting in isolation (e.g. sniper attacks).",
  },
  TMDisruptions: {
    details:
      "This item documents the extent to which a youth has had one or more major changes in caregivers, potentially resulting in disruptions in attachment.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that the youth has experienced disruptions in caregiving and/or attachment losses. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = Youth has been exposed to, or there is suspicion that he/she has been exposed to at least one disruption in caregiving familiar alternative caregivers or unknown caregivers (this includes placement in foster or other out-of-home care such as residential care facilities). Youth may or may not have had ongoing contact with primary attachment figure(s) during this disruption. Shift in caregiving may have been temporary or permanent. ",
      },
    ],
    considerations:
      "Has the youth ever lived apart from his/her parents/caregivers? Has the youth ever lived apart from his/her parents/caregivers? What happened that resulted in the youth living apart from his/her parents/caregivers?",
    supplemental:
      "Youth has been exposed to disruptions in caregiving involving separation from primary attachment figure(s) and/or attachment losses. Youth who have had placement changes, including stays in foster care, residential treatment facilities or juvenile justice settings, can be rated here. Short-term hospital stays or brief juvenile detention stays, during which the youth's caregiver remains the same, would not be rated on this item.",
  },
  TMParental: {
    details:
      "This item rates the criminal behavior of both biological and stepparents, and other legal guardians, not foster parents.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0|N) = There is no evidence that youth’s parents have ever engaged in criminal behavior. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1|Y) = One or both of the youth’s parents/guardians have a history of criminal behavior that resulted in a conviction or incarceration. A suspicion that one or both of the youth’s parents/guardians have a history of criminal behavior that resulted in conviction or incarceration would be rated here. ",
      },
    ],
    considerations:
      "Has the youth’s parents/guardian or family been involved in criminal activities or even been in jail?",
  },
  TMDysregulation: {
    details:
      "Youth has difficulties with arousal regulation or expressing emotions and energy states.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth has no difficulties regulating emotional or physiological responses. Emotional responses and energy level are appropriate to the situation. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History or evidence of difficulties with affect/physiological regulation. The youth could have some difficulty tolerating intense emotions and become somewhat jumpy or irritable in response to emotionally charged stimuli, or more watchful or hypervigilant in general or have some difficulties with regulating body functions (e.g. sleeping, eating or elimination). The youth may also have some difficulty sustaining involvement in activities for any length of time or have some physical or somatic complaints. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth has problems with affect/physiological regulation that are impacting his/her functioning in some life domains, but is able to control affect at times. The youth may be unable to modulate emotional responses or have more persistent difficulties in regulating bodily functions. The youth may exhibit marked shifts in emotional responses (e.g. from sadness to irritability to anxiety) or have contained emotions with a tendency to lose control of emotions at various points (e.g. normally restricted affect punctuated by outbursts of anger or sadness). The youth may also exhibit persistent anxiety, intense fear or helplessness, lethargy/loss of motivation, or affective or physiological over-arousal or reactivity (e.g. silly behavior, loose active limbs) or ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth is unable to regulate affect and/or physiological responses. The youth may have more rapid shifts in mood and an inability to modulate emotional responses (feeling out of control of their emotions or lacking control over their movement as it relates to their emotional states). The youth may also exhibit tightly contained emotions with intense outbursts under stress. Alternately, the youth may be characterized by extreme lethargy, loss of motivation or drive, and no ability to concentrate or sustain engagement in activities (i.e. emotionally “shut down”). The youth may have more persistent and severe difficulties regulating sleep/wake cycle, eating patterns, or have elimination problems. ",
      },
    ],
    considerations:
      "Does the youth have reactions that seem out of proportion to the situation? Does the youth have extreme or unchecked emotional reactions to situations?",
  },
  TMIntrusions: {
    details:
      "These symptoms consist of intrusive memories or reminders of traumatic events, including nightmares, flashbacks, intense reliving of the events, and repetitive play with themes of specific traumatic experiences.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. There is no evidence that youth experiences intrusive thoughts of trauma. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History or evidence of some intrusive thoughts of trauma but it does not affect the youth’s functioning. A youth with some problems with intrusive, distressing memories, including occasional nightmares about traumatic events, would be rated here. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth has difficulties with intrusive symptoms/distressing memories, intrusive thoughts that interfere in his/her ability to function in some life domains. For example, the youth may have recurrent frightening dreams with or without recognizable content or recurrent distressing thoughts, images, perceptions or memories of traumatic events. The youth may exhibit trauma specific reenactments through repetitive play with themes of trauma or intense physiological reactions to exposure to traumatic cues. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth has repeated and/or severe intrusive symptoms/distressing memories that are debilitating. This youth may exhibit trauma-specific reenactments that include sexually or physically traumatizing other children or sexual play with adults. This youth may also exhibit persistent flashbacks, illusions or hallucinations that make it difficult for the youth to function. ",
      },
    ],
    considerations:
      "Does the youth experience intrusions? If so, when and how often do they occur?",
  },
  TMHyperarousal: {
    details:
      "This includes difficulty falling asleep, irritability or outbursts of anger, difficulty concentrating, hyper vigilance and/or exaggerated startle response. Youth may also show common physical symptoms such as stomachaches and headaches. These symptoms are a part of the DSM-5 criteria for Trauma-Related Adjustment Disorder, Posttraumatic Stress Disorder and other Trauma- and Stressor Related Disorders.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth has no evidence of hyperarousal symptoms. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. History or evidence of hyperarousal that does not interfere with his/her daily functioning. Youth may occasionally manifest distress-related physical symptoms such as stomachaches and headaches. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth exhibits one significant symptom or a combination or two or more of the following hyperarousal symptoms: difficulty falling or staying asleep, irritability or outbursts of anger, difficulty concentrating, hyper vigilance and/or exaggerated startle response. Youth who frequently manifest distress-related physical symptoms such as stomach aches and headaches would be rated here. Symptoms are distressing for the youth and/ or caregiver and negatively impacts day-to-day functioning. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth exhibits multiple and/or severe hyperarousal symptoms including alterations in arousal and physiological and behavioral reactivity associated with traumatic event(s). This may include difficulty falling or staying asleep, irritability or outbursts of anger, difficulty concentrating, hyper vigilance and/or exaggerated startle response. Intensity and frequency of these symptoms are overwhelming for the youth and/or caregiver and impede day-to-day functioning in many life areas. ",
      },
    ],
    considerations:
      "Does the youth feel more jumpy or irritable than is usual? Does the youth have difficulty relaxing and/or have an exaggerated startle response? Does the youth have stress-related physical symptoms: stomach or headaches? Do these stress-related symptoms interfere with the youth’s ability to function?",
  },
  TMGrief: {
    details:
      "This rating describes the level of traumatic grief the youth is experiencing due to death or loss/separation from significant caregivers, siblings, or other significant figures.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. There is no evidence that the youth is experiencing traumatic grief or separation from the loss of significant caregivers. Either the youth has not experienced a traumatic loss (e.g., death of a loved one) or the youth has adjusted well to separation. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Youth is experiencing traumatic grief due to death or loss/separation from a significant person in a manner that is expected and/or appropriate given the recent nature of loss or separation. History of traumatic grief symptoms would be rated here. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth is experiencing traumatic grief or difficulties with separation in a manner that impairs functioning in some but not all areas. This could include withdrawal or isolation from others or other problems with day-to-day functioning. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth is experiencing dangerous or debilitating traumatic grief reactions that impair his/her functioning across several areas (e.g. interpersonal relationships, school) for a significant period of time following the loss or separation. Symptoms require immediate or intensive intervention. ",
      },
    ],
    considerations:
      "Is the trauma reaction of the youth based on a grief/loss experience? How much does the youth’s reaction to the loss impact functioning?",
  },
  TMNumbing: {
    details:
      "This item describes youth’s reduced capacity to feel or experience and express a range of emotions. These numbing responses were not present before the trauma.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. Youth has no evidence of numbing responses ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Youth has history or evidence of problems with numbing. He/she may have a restricted range of affect or be unable to express or experience certain emotions (e.g., anger or sadness). ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth exhibits numbing responses that impair his/her functioning in at least one life domain. Youth may have a blunted or flat emotional state or have difficulty experiencing intense emotions or feel consistently detached or estranged from others following the traumatic experience. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth exhibits significant numbing responses or multiple symptoms of numbing that put him/her at risk. This youth may have a markedly diminished interest or participation in significant activities and a sense of a foreshortened future. ",
      },
    ],
    considerations:
      "Does the youth experience a normal range of emotions? Does the youth tend to have flat emotional responses?",
  },
  TMDissociation: {
    details:
      "This item rates the level of dissociative states the youth may experience.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No current need; no need for action or intervention. No evidence of dissociation. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Youth has history or evidence of dissociative problems, including some emotional numbing, avoidance or detachment, and some difficulty with forgetfulness, daydreaming, spacing or blanking out. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth exhibits dissociative problems that interfere with functioning in at least one life domain. This can include amnesia for traumatic experiences or inconsistent memory for trauma (e.g., remembers in one context but not another), more persistent or perplexing difficulties with forgetfulness (e.g., loses things easily, forgets basic information), frequent daydreaming or trance like behavior, depersonalization and/or derealization. This rating would be used for someone who meets criteria for Dissociative Disorders or another diagnosis that is specified “with dissociative features” ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth exhibits dangerous and/or debilitating dissociative symptoms. This can include significant memory difficulties associated with trauma that also impede day to day functioning. Youth is frequently forgetful or confused about things he/she should know about (e.g., no memory for activities or whereabouts of previous day or hours). Youth shows rapid changes in personality or evidence of distinct personalities. Youth who meets criteria for Dissociative Identity Disorder or a more severe level of a Dissociative Disorder would be rated here. ",
      },
    ],
    considerations:
      "Does the youth ever enter a dissociative state? Does the youth often become confused about who or where they are? Has the youth been diagnosed with a dissociative disorder",
  },
  TMAvoidance: {
    details:
      "These symptoms include efforts to avoid stimuli associated with traumatic experiences. These symptoms are part of the DSM criteria for PTSD and Acute Stress Disorder",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any needs. Youth exhibits no avoidance symptoms. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Need that requires monitoring, watchful waiting, or preventive action. This may have been a risk behavior in the past. Youth may have history or exhibits one primary avoidant symptom, including efforts to avoid thoughts, feelings or conversations associated with the trauma. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Action or intervention is required to ensure that the identified need is addressed. Youth exhibits avoidance symptoms that interfere with his/her functioning in at least one life domain. In addition to avoiding thoughts or feelings associated with the trauma, the youth may also avoid activities, places, or people that arouse recollections of the trauma. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Intensive and/or immediate action is required to address the need or risk behavior. Youth’s avoidance symptoms are debilitating. Youth may avoid thoughts, feelings, situations and people associated with the trauma and is unable to recall important aspects of the trauma. ",
      },
    ],
    considerations:
      "Does the youth make specific and concerted attempts to avoid sights, sounds, smells, etc. that are related to the trauma experience?",
  },
  SUDMSeverity: {
    details:
      "This item rates the frequency and severity of the youth’s current substance use.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth is currently abstinent and has maintained abstinence for at least six months. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth is currently abstinent but only in the past 30 days or youth has been abstinent for more than 30 days but is living in an environment that makes substance use difficult. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth actively uses alcohol or drugs but not daily. ",
      },
      {
        _id: "3",
        name: "3",
        description: "(3) = Youth uses alcohol and/or drugs on a daily basis. ",
      },
    ],
    considerations:
      "Is the youth currently using substances? If so, how frequently? Is there evidence of physical dependence on substances?",
  },
  SUDMDuration: {
    details:
      "This item identifies the length of time that the youth has been using drugs or alcohol.",
    options: [
      {
        _id: "0",
        name: "0",
        description: "(0) = Youth has begun use in the past year. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has been using alcohol or drugs for at least one year but has had periods of at least 30 days where the youth did not have any use. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has been using alcohol or drugs for at least one year (but less than five years), but not daily. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has been using alcohol or drugs daily for more than the past year or intermittently for at least five years. ",
      },
    ],
    considerations: "How long has the youth been using drugs and/or alcohol?",
  },
  SUDMStage: {
    details:
      "This item identifies where the youth is in the youth’s recovery process",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth is in maintenance stage of recovery. Youth is abstinent and able to recognize and avoid risk factors for future alcohol or drug use. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth is actively trying to use treatment to remain abstinent. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth is in contemplation phase, recognizing a problem but not willing to take steps for recovery. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth is in denial regarding the existence of any substance use problem. ",
      },
    ],
    considerations:
      "In relation to stopping substance use, at what stage of change is the youth?",
  },
  SUDMPeer: {
    details:
      "This item identifies the impact that the youth’s social group has on the youth’s substance use.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth's primary peer social network does not engage in alcohol or drug use. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has peers in the youth’s primary peer social network who do not engage in alcohol or drug use but has some peers who do. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth predominantly has peers who engage in alcohol or drug use but youth is not a member of a gang. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth is a member of a peer group that consistently engages in alcohol or drug use. ",
      },
    ],
    considerations:
      "What role do the youth’s peers play in their alcohol and drug use?",
  },
  SUDMParental: {
    details:
      "This item rates the parent’s/caregiver’s use of drugs or alcohol with or in the presence of the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = There is no evidence that youth's caregivers have ever engaged in substance abuse. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = One of youth's caregivers has history of substance abuse but not in the past year. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = One or both of youth’s caregivers have been intoxicated with alcohol or drugs in the presence of the youth. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = One or both of youth's caregivers use alcohol or drugs with the youth. ",
      },
    ],
    considerations:
      "Do the caregiver(s) use substances? If so, does the caregiver’s use impact the youth’s use?",
  },
  SUDMEnvironmental: {
    details:
      "This item rates the impact of the youth’s community environment on their alcohol and drug use.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence that the youth's environment stimulates or exposes the youth to any alcohol or drug use. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Mild problems in the youth's environment that might expose the youth to alcohol or drug use. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Moderate problems in the youth's environment that clearly expose the youth to alcohol or drug use. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Severe problems in the youth's environment that stimulate the youth to engage in alcohol or drug. ",
      },
    ],
    considerations:
      "Are there factors in the youth’s community that impacts their alcohol and drug use?",
  },
  VMPhysical: {
    details:
      "This item rates the history of physical abuse the youth has received",
    options: [
      {
        _id: "0",
        name: "0",
        description: "(0) = No evidence of a history of physical abuse. ",
      },
      {
        _id: "1",
        name: "1",
        description: "(1) = Youth has experienced corporal punishment. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has experienced physical abuse on one or more occasions from care giver or parent. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has experienced extreme physical abuse that has resulted in physical injuries that required medical care. ",
      },
    ],
    considerations: "Has the youth ever been physically abused?",
  },
  VMHistory: {
    details: "This item rates the youth’s history of violence.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any history of violent behavior by the youth. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has engaged in mild forms of violent behavior including vandalism, minor destruction of property, physical fights in which no one was injured (e.g. shoving, wrestling). ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has engaged in moderate forms of violent behavior including fights in which participants were injured. Cruelty to animals would be rated here unless it resulted in significant injury or death of the animal. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has initiated unprovoked violent behaviors on other people that resulted in injuries to these people. Cruelty to animals that resulted in significant injury or death to the animal would be rated here. ",
      },
    ],
    considerations:
      "Has the youth ever been violent with a sibling, peer, and adult?",
  },
  VMDomestic: {
    details:
      "This item rates the extent of domestic violence the youth has witnessed.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence that youth has witnessed domestic violence. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has witnessed physical violence in household on at least one occasion but the violence did not result in injury. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has witnessed repeated domestic violence that has resulted in the injury of at least one family member that required medical treatment. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has witness to murder or rape of a family member. ",
      },
    ],
    considerations:
      "Has the youth ever witnessed violence in the home? Has a family member needed to be hospitalized or passed away?",
  },
  VMEnvironmental: {
    details:
      "This item rates the extent of violence the youth has witnessed in their community/environment.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence that youth has witnessed violence in the youth’s environment and does not watch an excessive amount of violent media. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has not witness violence in her environment and but watches an excessive amount of violent media including movies and video games. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has witnessed at least one occasion of violence in the youth’s environment. ",
      },
      {
        _id: "3",
        name: "3",
        description: "(3) = Youth has witnessed a murder or rape. ",
      },
    ],
    considerations:
      "Has the youth ever witnessed violence in their environment?",
  },
  VMBullying: {
    details:
      "This item describes perpetrators of the exploitation of others. Generally, this refers to bullying other children or youth (usually smaller or younger ones); however, it could include youth who bully adults.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth has never engaged in bullying at school or in the community. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has been involved with groups that have bully other youth either in school or the community; however, youth has not had a leadership role in these groups. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has bullied other youth in school or community. Youth has either bullied the other youth individually or led a group that bullied youth. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has repeated utilized threats or actual violence to bully youth in school and/or community. ",
      },
    ],
    considerations:
      "Have there been any reports that the youth has picked on, made fun or, harassed or intimidated another person? Are there concerns that the youth might bully other children? Does the youth hang around with other people who bully?",
  },
  VMFrustration: {
    details:
      "This item describes the youth’s ability to manage their own anger and frustration tolerance.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth appears to be able to manage frustration well. No evidence of problems of frustration management. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has some mild problems with frustration. The youth may anger easily when frustrated; however, the youth is able to calm self-down following an angry outburst. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has problems managing frustration. The youth’s anger when frustrated is causing functioning problems in school, at home, or with peers. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth becomes explosive and dangerous to others when frustrated. The youth demonstrates little self-control in these situations and others must intervene to restore control. ",
      },
    ],
    considerations:
      "How does the youth control the youth’s temper? Does the youth get upset or frustrated easily? Does the youth become physically aggressive when angry? Does the youth have a hard time managing anger if someone criticizes or rejects the youth?",
  },
  VMHostility: {
    details:
      "This item rates the perception of others regarding the youth’s level of anger and hostility.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth appears to not experience or express hostility except in situations where most people would become hostile. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth appears hostile but does not express it. Others experience youth as being angry. ",
      },
      {
        _id: "2",
        name: "2",
        description: "(2) = Youth expresses hostility regularly. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth is almost always hostile either in expression or appearance. Others may experience youth as ‘full of rage’ or ‘seething’. ",
      },
    ],
    considerations:
      "Does the youth seem hostile frequently or in inappropriate environments/ situations?",
  },
  VMParanoia: {
    details:
      "This item rates the existence/level of paranoid thinking experienced by the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth does not appear to engage in any paranoid thinking. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth is suspicious of others but is able to test out these suspicions and adjust their thinking appropriately. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth believes that others are ‘out to get’ the youth. Youth has trouble accepting that these beliefs may not be accurate. Youth at times is suspicious and guarded but at other times can be open and friendly. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth believes that others plan to cause them harm. Youth is nearly always suspicious and guarded. ",
      },
    ],
    considerations:
      "Does the youth seem suspicious? Is there any evidence of paranoid thinking/beliefs? Is the youth very guarded?",
  },
  VMGains: {
    details:
      "This item is used to rate the presence of anger to obtain additional benefits.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth either does not engage in angry behavior or, when they do become angry, does not appear to derive any benefits from this behavior. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth unintentionally has benefited from angry behavior; however, there is no evidence that youth intentionally uses angry behavior to achieve desired outcomes. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth sometimes uses angry behavior to achieve desired outcomes with parents, caregivers, teachers, or peers. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth routinely uses angry behavior to achieve desired outcomes with parents, caregivers, teachers, or peers. Others in youth’s life appear intimidated. ",
      },
    ],
    considerations:
      "What happens after the youth gets angry? Does the youth get anything in return? Does the youth typically get what the youth wants from expressing anger?",
  },
  VMViolent: {
    details:
      "This item rates the level of violence and aggression in the youth’s thinking",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = There is no evidence that youth engages in violent thinking. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has some occasional or minor thoughts about violence. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has violent ideation. Language is often characterized as having violent themes and problem solving often refers to violent outcomes. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has specific homicidal ideation or appears obsessed with thoughts about violence. For example, a youth who spontaneously and frequently draws only violent images may be rated here. ",
      },
    ],
    considerations:
      "Does the youth report having violent thoughts? Does the youth verbalize the youth’s violent thoughts either specifically or by using violent themes?",
  },
  VMAwareness: {
    details: "This item rates the youth’s insight into their risk of violence",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth is completely aware of the youth’s level of risk of violence. Youth knows and understands risk factors. Youth accepts responsibility for past and future behaviors. Youth is able to anticipate future challenging circumstances. A youth with no violence potential would be rated here. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth is generally aware of the youth’s potential for violence. Youth is knowledgeable about the youth’s risk factors and is generally able to take responsibility. Youth may be unable to anticipate future circumstances that may challenge the youth. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has some awareness of the youth’s potential for violence. Youth may have tendency to blame others but is able to accept some responsibility for the youth’s actions. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has no awareness of the youth’s potential for violence. Youth may deny past violent acts or explain them in terms of justice or as deserved by the victim. ",
      },
    ],
    considerations:
      "Is the youth aware of the risks of their potential to be violent? Is the youth concerned about these risks? Can the youth predict when/where/for what reason the youth will get angry and/or possibly become violent?",
  },
  VMResponse: {
    details:
      "This item rates the youth’s reaction when the youth gets consequences for violence or aggression.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth is clearly and predictably responsive to identified consequences. Youth is regularly able to anticipate consequences and adjust behavior. ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth is generally responsive to identified consequences; however, not all appropriate consequences have been identified or the youth may sometimes fail to anticipate consequences. ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth responds to consequences on some occasions but sometimes does not appear to care about consequences for the youth’s violent behavior. ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth is unresponsive to consequences for the youth’s violent behavior.  ",
      },
    ],
    considerations:
      "How does the youth react to consequences given for violent or aggressive behavior?",
  },
  VMCommitment: {
    details:
      "This item rates the youth’s willingness and commitment to controlling aggressive and/or violent behaviors.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth fully committed to controlling the youth’s violent behavior.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth is generally committed to control the youth’s violent behavior; however, youth may continue to struggle with control in some challenging circumstances.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth ambivalent about controlling the youth’s violent behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth not interested in controlling the youth’s violent behavior at this time.  ",
      },
    ],
    considerations:
      "Does the youth want to change the youth’s behaviors? Is the youth committed to such change?",
  },
  VMTreatment: {
    details:
      "This item rates the youth and/or family’s involvement in their treatment.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth fully involved in the youth’s own treatment. Family supports treatment as well.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth or family involved in treatment but not both. Youth may be somewhat involved in treatment, while family members are active or youth may be very involved in treatment while family members are unsupportive.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth and family are ambivalent about treatment involvement. Youth and/or family may be skeptical about treatment effectiveness or suspicious about clinician intentions.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth and family are uninterested in treatment involvement. A youth with treatment needs who is not currently in treatment would be rated here.  ",
      },
    ],
    considerations:
      "Is the youth on medication or have a treatment plan? Does the youth and family know what the plan is?",
  },
  SABMRelationship: {
    details:
      "This item rates the nature of the relationship between the youth and the victim of their aggression.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of victimizing others. All parties in sexual activity appear to be consenting. No power differential.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Although parties appear to be consenting, there is a significant power differential between parties in the sexual activity with this youth being in the position of authority.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth is clearly victimizing at least one other individual with sexually abusive behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth is severely victimizing at least one other individual with sexually abusive behavior. This may include physical harm that results from either the sexual behavior or physical force associated with sexual behavior.  ",
      },
    ],
    considerations:
      "How does the youth know the other children involved? Is there a power differential between parties? Did the sexual aggression include physical harm to another person?",
  },
  SABMThreat: {
    details:
      "This item rates the level of physical force involved in the sexual aggression.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of the use of any physical force or threat of force in either the commission of the sex act nor in attempting to hide it.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Evidence of the use of the threat of force in an attempt to discourage the victim from reporting the sex act.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Evidence of the use of mild to moderate force in the sex act. There is some physical harm or risk of physical harm.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Evidence of severe physical force in the commission of the sex act. Victim harmed or at risk for physical harm from the use of force.  ",
      },
    ],
    considerations:
      "Did the sex act include physical force or the threat of force? If so, how intense was that force? Was the victim physically harmed or at risk of serious harm?",
  },
  SABMPlanning: {
    details: "This item should be rated only for the perpetrator.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any planning. Sexual activity appears entirely opportunistic.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Some evidence of efforts to get into situations where likelihood of opportunities for sexual activity are enhanced.  ",
      },
      {
        _id: "2",
        name: "2",
        description: "(2) = Evidence of some planning of sex act.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Considerable evidence of predatory sexual behavior in which victim is identified prior to the act, and the act is premeditated.  ",
      },
    ],
    considerations:
      "Does the youth plan the their sexual activities, or do they happen spontaneously?",
  },
  SABMAge: {
    details:
      "Please rate the highest level from the most recent episode of sexual behavior. This item should be rated only for the perpetrator.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Ages of the perpetrator and victim and/or participants essentially equivalent (less than 3 years apart).  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Age differential between perpetrator and victim and/or participants is 3 to 4 years.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Age differential between perpetrator and victim at least 5 years, but perpetrator less than 13 years old.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Age differential between perpetrator and victim at least 5 years and perpetrator 13 years old or older.  ",
      },
    ],
    considerations:
      "What are the ages of the individuals the youth has had sex with?",
  },
  SABMType: {
    details:
      "This item rates the kind of the sex act involved in the aggression. Rate the most serious type of aggression present.",
    options: [
      {
        _id: "0",
        name: "0",
        description: "(0) = Sex act(s) involve touching or fondling only.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Sex act(s) involve fondling plus possible penetration with fingers or oral sex.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Sex act(s) involve penetration into genitalia or anus with body part.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Sex act involves physically dangerous penetration due to differential size or use of an object.  ",
      },
    ],
    considerations:
      "What was the exact sex act(s) involved in the youth’s aggression?",
  },
  SABMResponse: {
    details:
      "This item rates how the youth responded to the accusation and the remorse felt by the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth admits to behavior and expresses remorse and desire to not repeat.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth partially admits to behaviors and expresses some remorse.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth admits to behavior but does not express remorse.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth neither admits to behavior nor expresses remorse. Youth is in complete denial.  ",
      },
    ],
    considerations:
      "Is the youth sorry for their behavior? Does the youth admit to the sex acts?",
  },
  SABMConsistency: {
    details:
      "Temporal consistency relates to a youth's patterns and history of sexually problematic behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = This level indicates a youth who has never exhibited sexually abusive behavior or who has developed this behavior only in the past three months following a clear stressor.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = This level indicates a youth who has been sexually abusive during the past two years OR youth who has become sexually abusive in the past three months despite the absence of any clear stressors.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = This level indicates a youth who has been sexually abusive for an extended period of time (e.g. more than two years), but who has had significant symptom-free periods.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = This level indicates a youth who has been sexually abusive for an extended period of time (e.g. more than two years) without significant symptom-free periods.  ",
      },
    ],
    considerations:
      "How long has the youth exhibited sexually problematic behavior(s)?",
  },
  SABMHistory: {
    details:
      "This item rates the quantity of sexually aggressive behaviors exhibited by the youth.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth or adolescent has only one incident of sexually abusive behavior that has been identified and/or investigated.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth or adolescent has two or three incidents of sexually abusive behavior that have been identified and/or investigated.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth or adolescent has four to ten incidents of sexually abusive behavior that have been identified and/or investigated with more than one victim.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth or adolescent has more than ten incidents of sexually abusive behavior with more than one victim.  ",
      },
    ],
    considerations:
      "How many incidents have been identified and/or investigated? How many victims have been identified?",
  },
  SABMSeverity: {
    details:
      "This item rates the significance and severity of the youth’s own sexual abuse history.",
    options: [
      {
        _id: "0",
        name: "0",
        description: "(0) = No history of any form of sexual abuse.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = History of occasional fondling or being touched inappropriately, however, not occurring on a regular basis or by someone in a caregiver capacity or suspicion of history of sexual abuse without confirming evidence.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = This level is to indicate a moderate level of sexual abuse. This may involve a youth who has been fondled on an ongoing basis or sexually penetrated (anal or genital) once by someone not in a caregiver capacity.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = This level is to indicate a severe level of sexual abuse involving penetration on an ongoing basis by someone either in a caregiver capacity or in close emotional relation to the youth.  ",
      },
    ],
    considerations:
      "Has the youth been sexually abused, either known or suspected? If so, what was the type and intensity of abuse the youth endured? If so, who was youth’s abuser?",
  },
  SABMPrior: {
    details:
      "This item rates the youth’s experience in and the effectiveness of prior treatment.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No history of prior treatment or history of outpatient treatment with notable positive outcomes.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = History of outpatient treatment which has had some degree of success.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = History residential treatment where there has been successful completion of program.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = History of residential or outpatient treatment condition with little or no success.  ",
      },
    ],
    considerations:
      "Does the youth have any history of treatment for sexual aggression? If so, what type of treatment and what was the effectiveness of each treatment?",
  },
  RMFrequency: {
    details: "This item describes how often the youth runs away.",
    options: [
      {
        _id: "0",
        name: "0",
        description: "(0) = Youth has only run once in past year.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has run on multiple occasions in past year.  ",
      },
      {
        _id: "2",
        name: "2",
        description: "(2) = Youth runs run often but not always.  ",
      },
      {
        _id: "3",
        name: "3",
        description: "(3) = Youth runs at every opportunity.  ",
      },
    ],
    considerations: "How often does the youth run away?",
  },
  RMConsistency: {
    details:
      "This item describes whether or not the youth runs away to the same place, area, or neighborhood.",
    options: [
      {
        _id: "0",
        name: "0",
        description: "(0) = Youth always runs to the same location.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth generally runs to the same location or neighborhood.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth runs to the same community but the specific locations change.  ",
      },
      {
        _id: "3",
        name: "3",
        description: "(3) = Youth runs to no planned destination.  ",
      },
    ],
    considerations: "Does the youth always run to the same spot?",
  },
  RMSafety: {
    details: "This item describes how safe the area is where the youth runs.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth runs to a safe environment that meets the youth’ basic needs (e.g. food, shelter).  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth runs to generally safe environments; however, they might be somewhat unstable or variable.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth runs to generally unsafe environments that cannot meet the youth’s basic needs.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth runs to very unsafe environments where the likelihood that the youth will be victimized is high.  ",
      },
    ],
    considerations: "Does the youth run to safe locations?",
  },
  RMIllegal: {
    details:
      "This item describes what type of activities the youth is involved in while on the run and whether or not they are legal activities.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth does not engage in illegal activities while on run beyond those involved with the running itself.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth engages in status offenses beyond those involved with the running itself while on run (e.g. curfew violations, underage drinking)  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth engages in delinquent activities while on run.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth engages in dangerous delinquent activities while on run (e.g. prostitution).  ",
      },
    ],
    considerations:
      "When the youth runs, is the youth involved in illegal acts?",
  },
  RMReturn: {
    details:
      "This item describes whether or not the youth returns from a running episode on their own, whether they need prompting, or whether they need to be brought back by force (police).",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth will return from run on the youth’s own without prompting.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth will return from run when found but not without being found.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth will make the youth difficult to find and/or might passively resist return once found.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth makes repeated and concerted efforts to hide so as to not be found and/or resists return.  ",
      },
    ],
    considerations: "Does the youth usually return home on their own?",
  },
  RMInvolvement: {
    details:
      "This item describes whether or not others help the youth to run away.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth runs by self with no involvement of others. Others may discourage behavior or encourage youth to return from run.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Others enable youth running by not discouraging youth’s behavior.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Others involved in running by providing helping youth not be found.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth actively is encouraged to run by others. Others actively cooperate to facilitate running behavior.  ",
      },
    ],
    considerations: "Are others involved in the running activities?",
  },
  RMExpectations: {
    details:
      "This item describes what the youth’s expectations are for when they run away.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth has realistic expectations about the implications of their running behavior.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has reasonable expectations about the implications of their running behavior but may be hoping for a somewhat ‘optimistic’ outcome.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has unrealistic expectations about the implications of their running behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has obviously false or delusional expectations about the implications of their running behavior.  ",
      },
    ],
    considerations:
      "Does the youth have realistic expectations when they run away?",
  },
  RMPlanning: {
    details:
      "This item describes how much planning the youth put into running away or if the youth runs spontaneously.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Running behavior is completely spontaneous and emotionally impulsive.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Running behavior is somewhat planned but not carefully.  ",
      },
      {
        _id: "2",
        name: "2",
        description: "(2) = Running behavior is planned.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Running behavior is carefully planned and orchestrated to maximize likelihood of not being found.  ",
      },
    ],
    considerations: "Does the youth plan when they run away?",
  },
  JJMHistory: {
    details: "This item rates the youth’s history of delinquency.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Current criminal behavior is the first known occurrence.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has engaged in multiple delinquent acts in the past one year.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has engaged in multiple delinquent acts for more than one year but has had periods of at least 3 months where the youth did not engage in delinquent behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has engaged in multiple criminal or delinquent acts for more than one year without any period of at least 3 months where the youth did not engage in criminal or delinquent behavior.  ",
      },
    ],
    considerations:
      "What are the behaviors/actions that have made the youth involved in the juvenile justice or adult criminal system?",
  },
  JJMSeriousness: {
    details:
      "This item rates the seriousness of the youth’s criminal offenses.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth has engaged only in status violations (e.g. curfew).  ",
      },
      {
        _id: "1",
        name: "1",
        description: "(1) = Youth has engaged in delinquent behavior.  ",
      },
      {
        _id: "2",
        name: "2",
        description: "(2) = Youth has engaged in criminal behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has engaged in delinquent criminal behavior that places other citizens at risk of significant physical harm.  ",
      },
    ],
    considerations:
      "What are the behaviors/actions that have made the youth involved in the juvenile justice or adult criminal system?",
  },
  JJMPlanning: {
    details:
      "This item rates the premeditation or spontaneity of the criminal acts.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any planning. Delinquent behavior appears opportunistic or impulsive.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Evidence suggests that youth places the youth self into situations where the likelihood of delinquent behavior is enhanced.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Evidence of some planning of delinquent behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Considerable evidence of significant planning of delinquent behavior. Behavior is clearly premeditated.  ",
      },
    ],
    considerations:
      "Does the youth engage in preplanned or spontaneous or impulsive criminal acts?",
  },
  JJMSafety: {
    details:
      "This item rates the level to which the criminal behavior of the youth puts the community’s safety at risk.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth presents no risk to the community. The youth could be unsupervised in the community.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth engages in behavior that represents a risk to community property.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth engages in behavior that places community residents in some danger of physical harm. This danger may be an indirect effect of the youth’s behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth engages in behavior that directly places community members in danger of significant physical harm.  ",
      },
    ],
    considerations:
      "Is the delinquency violent in nature? Does the youth commit violent crimes against people or property?",
  },
  JJMPeers: {
    details:
      "This item rates the level to which the youth’s peers engage in delinquent or criminal behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth's primary peer social network does not engage in delinquent behavior.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has peers in the youth’s primary peer social network who do not engage in delinquent behavior but has some peers who do.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth predominantly has peers who engage in delinquent behavior but youth is not a member of a gang.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth is a member of a gang whose membership encourages or requires illegal behavior as an aspect of gang membership.  ",
      },
    ],
    considerations:
      "Do the youth’s friends also engage in criminal behavior? Are the members of the youth’s peer group involved in the criminal justice system or on parole/probation?",
  },
  JJMParental: {
    details:
      "This item rates the influence of parental criminal behavior on the youth’s delinquent or criminal behavior",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = There is no evidence that youth's parents have ever engaged in criminal behavior.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = One of youth's parents has history of criminal behavior but youth has not been in contact with this parent for at least one year.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = One of youth's parents has history of criminal behavior and youth has been in contact with this parent in the past year.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = One of youth's parents has history of criminal behavior but youth has not been in contact with this parent for at least one year.  ",
      },
    ],
    considerations:
      "Have the youth’s parent(s) ever been arrested? If so, how recently has the youth seen his parent(s)?",
  },
  JJMEnvironmental: {
    details:
      "This item rates the influence of community criminal behavior on the youth’s delinquent or criminal behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence that the youth's environment stimulates or exposes the youth to any criminal behavior.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Mild problems in the youth's environment that might expose the youth to criminal behavior.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Moderate problems in the youth's environment that clearly expose the youth to criminal behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Severe problems in the youth's environment that stimulate the youth to engage in criminal behavior.  ",
      },
    ],
    considerations:
      "Does the youth live in a neighborhood/community with high levels of crime? Is the youth a frequent witness or victim of such crime?",
  },
  FSMHistory: {
    details:
      "This item rates the youth's history of fire setting including the number of fire setting events and the time elapsed between fire setting events.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Only one known occurrence of fire setting behavior.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has engaged in multiple acts of fire setting in the past year.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has engaged in multiple acts of fire setting for more than one year but has had periods of at least 6 months where the youth did not engage in fire setting behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has engaged in multiple acts of fire setting for more than one year without any period of at least 3 months where the youth did not engage in fire setting behavior.  ",
      },
    ],
    considerations:
      "How many times have you started fires? When did that happen?",
  },
  FSMSeriousness: {
    details:
      "This item rates the extent of damage or harm caused by the youth's fire setting behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth has engaged in fire setting that resulted in only minor damage (e.g. camp fire in the back yard which scorched some lawn).  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth has engaged in fire setting that resulted only in some property damage that required repair.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth has engaged in fire setting which caused significant damage to property (e.g. burned down house).  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth has engaged in fire setting that injured self or others.  ",
      },
    ],
    considerations:
      "What happened after you started fires? What was the extent of the damage? Was any property damaged or were there any injuries?",
  },
  FSMPlanning: {
    details:
      "This item rates the youth's forethought when engaging in fire setting behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any planning. Fire setting behavior appears opportunistic or impulsive.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Evidence suggests that youth places the youth self into situations where the likelihood of fire setting behavior is enhanced.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Evidence of some planning of fire setting behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Considerable evidence of significant planning of fire setting behavior. Behavior is clearly premeditated.  ",
      },
    ],
    considerations:
      "This item rates the youth's forethought when engaging in fire setting behavior.",
  },
  FSMAccelerants: {
    details:
      "This item rates the youth's use of chemicals and other flammable materials (accelerants) to aid the spread of fire or to make the fire more intense.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = No evidence of any use of accelerants (e.g., gasoline). Fire setting involved only starters such as matches or a lighter.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Evidence suggests that the fire setting involved some use of mild accelerants (e.g. sticks, paper) but no use of liquid accelerants.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Evidence that fire setting involved the use of a limited amount of liquid accelerants but that some care was taken to limit the size of the fire.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Considerable evidence of significant use of accelerants in an effort to secure a very large and dangerous fire.  ",
      },
    ],
    considerations:
      "Have you used accelerants to start a fire, such as gasoline or anything that will help you start a fire rapidly?",
  },
  FSMIntention: {
    details:
      "This item rates the extent to which the youth intended to injure others when fire setting.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth did not intend to harm others with fire. The youth took efforts to maintain some safety.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth did not intend to harm others but took no efforts to maintain safety.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth intended to seek revenge or scare others but did not intend physical harm, only intimidation.  ",
      },
      {
        _id: "3",
        name: "3",
        description: "(3) = Youth intended to injure or kill others.  ",
      },
    ],
    considerations:
      "When you started the fire, did you intend to harm/injure or kill someone? Were you seeking revenge?",
  },
  FSMSafety: {
    details:
      "This item rates the level of risk the youth poses to the community due to the youth’s fire setting behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth presents no risk to the community. The youth could be unsupervised in the community.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth engages in fire setting behavior that represents a risk to community property.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth engages in fire setting behavior that places community residents in some danger of physical harm. This danger may be an indirect effect of the youth’s behavior.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth engages in fire setting behavior that intentionally places community members in danger of significant physical harm. Youth attempts to use fires to hurt others.  ",
      },
    ],
    considerations:
      "When you started the fires, did you place other people in your community at risk? Do other people think that you put them at risk when you start fires? Do you intentionally try to hurt others when you start a fire?",
  },
  FSMResponse: {
    details:
      "This item rates the reaction of the youth as the youth is confronted with the behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth admits to behavior and expresses remorse and desire to not repeat.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth partially admits to behaviors and expresses some remorse.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth admits to behavior but does not express remorse.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth neither admits to behavior nor expresses remorse. Youth is in complete denial.  ",
      },
    ],
    considerations:
      "How did you react when you were accused of setting fires? How do you feel about that?",
  },
  FSMRemorse: {
    details:
      "This item rates the degree to which the youth expresses regret for the behavior.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth accepts responsibility for behavior and is truly sorry for any damage/risk caused. Youth is able to apologize directly to effected people.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth accepts responsibility for behavior and appears to be sorry for any damage/risk caused. However, youth is unable or unwilling to apologize to effected people.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth accepts some responsibility for behavior but also blames others. May experience sorrow at being caught or receiving consequences. May express sorrow/remorse but only in an attempt to reduce consequences.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth accepts no responsibility and does not appear to experience any remorse.  ",
      },
    ],
    considerations:
      "Does the youth feel responsible for starting that fire? How did the youth apologize for what they did?",
  },
  FSMFuture: {
    details:
      "This item rates the potential for reoccurrence of fire setting behavior in the future.",
    options: [
      {
        _id: "0",
        name: "0",
        description:
          "(0) = Youth is unlikely to set fires in the future. Youth able and willing to exert self-control over fire setting.  ",
      },
      {
        _id: "1",
        name: "1",
        description:
          "(1) = Youth presents mild to moderate risk of fire setting in the future. Should be monitored but does not require ongoing treatment/intervention.  ",
      },
      {
        _id: "2",
        name: "2",
        description:
          "(2) = Youth remains at risk of fire setting if left unsupervised. Youth struggles with self-control.  ",
      },
      {
        _id: "3",
        name: "3",
        description:
          "(3) = Youth presents a real and present danger of fire setting in the immediate future. Youth unable or unwilling to exert self-control over fire setting behavior.  ",
      },
    ],
    considerations:
      "How is the youth willing to control the youth’s self to prevent setting fires in the future?",
  },
};

function ALCANS() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (data, cData) => {
    if (!data.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(data.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(data.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(true);
  const [showDefinitions, setShowDefinitions] = useState(false);

  const dateOfAssessment = useMemo(() => {
    return getFieldValue(data.DateOfAssessment);
  }, [data]);

  const previousAssessment = useMemo(() => {
    const previous = allFormSubmissions
      .filter((f) => {
        if (f.SubmissionId === formsyDetails.SubmissionId) return false;

        if (!dateOfAssessment) return false;

        const assessmentDate = moment(dateOfAssessment);
        const fDateOfAssessment = moment(
          getFieldValue(f.FormResults.results.DateOfAssessment)
        );
        return fDateOfAssessment.isBefore(assessmentDate);
      })
      .sort((a, b) => {
        const aData = a.FormResults.results;
        const bData = b.FormResults.results;
        const aStart = moment(getFieldValue(aData.DateOfAssessment));
        const bStart = moment(getFieldValue(bData.DateOfAssessment));

        if (aStart.isAfter(bStart)) return 1;
        if (bStart.isAfter(aStart)) return -1;
        return 0;
      })[0];

    return previous?.FormResults.results;
  }, [allFormSubmissions, formsyDetails, dateOfAssessment]);

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const QuestionDefinition = (fieldName) => {
    const definition = definitions[fieldName];

    return definition ? (
      <div className="container bg-safy-info2 rounded mb-2">
        <div className="row">
          <div className="col mt-2 mb-3">
            <div className="fw-bold">
              {getFieldLabel(formConfig[fieldName])}
            </div>
            <p>{definition.details}</p>
            <ul>
              {definition.options.map((option) => (
                <li
                  key={option._id}
                  style={{
                    paddingLeft: "1.5em",
                    textIndent: "-1.5em",
                  }}
                >
                  {option.description}
                </li>
              ))}
            </ul>
            <div className="mt-2 fw-bold">Considerations:</div>
            <div>{definition.considerations}</div>
          </div>
        </div>
      </div>
    ) : null;
  };

  const Question = (fieldName) => {
    const questionOptions = definitions[fieldName]?.options || [];

    return (
      <React.Fragment key={fieldName}>
        <div className="row border-top">
          <div className="col-6 py-2 border-end">
            {showDefinitions ? (
              <div>{getFieldLabel(formConfig[fieldName])}</div>
            ) : (
              <div
                data-tooltip-id="definition-tooltip"
                data-tooltip-html={ReactDOMServer.renderToString(
                  QuestionDefinition(fieldName)
                )}
              >
                {getFieldLabel(formConfig[fieldName])}
              </div>
            )}
            {errors[fieldName] ? (
              <div className="mt-1 alert alert-danger">
                {getFieldLabel(formConfig[fieldName])} is required
              </div>
            ) : null}
          </div>
          {fieldName.includes("Comments") ? (
            <div className="col py-2">
              <FormInput
                name={fieldName}
                value={getFieldValue(data[fieldName])}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
            </div>
          ) : (
            <>
              {previousAssessment ? (
                <div className="col-2 py-2">
                  <FormInput
                    name={fieldName}
                    value={getFieldValue(previousAssessment[fieldName])}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={true}
                  />
                </div>
              ) : null}
              <div className="col py-2">
                <FormSelect
                  name={fieldName}
                  options={questionOptions}
                  value={getFieldValue(data[fieldName])}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>
              <div className="col-2"></div>
            </>
          )}
        </div>
        {showDefinitions ? QuestionDefinition(fieldName) : null}
      </React.Fragment>
    );
  };

  const getCategoryFields = (prefix) => {
    return Object.keys(data).filter((key) => key.startsWith(prefix));
  };

  const getProgress = (prefix) => {
    const fields = getCategoryFields(prefix).filter(
      (field) => !field.includes("Comments")
    );
    const completed = fields.filter((field) => {
      const value = getFieldValue(data[field]);
      return value !== undefined && value !== "";
    });

    return Math.round((completed.length / fields.length) * 100);
  };

  const getScore = (dataObj, prefix) => {
    if (!dataObj) {
      return;
    }

    const fields = getCategoryFields(prefix).filter(
      (field) =>
        !field.includes("Comments") && !isNaN(getFieldValue(dataObj[field]))
    );

    if (fields.length === 0) {
      return 0;
    }

    let total = 0;
    fields.forEach(
      (field) => (total += parseInt(getFieldValue(dataObj[field])))
    );
    return total;
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DateOfAssessment"
                    label={getFieldLabel(formConfig.DateOfAssessment)}
                    error={errors.DateOfAssessment}
                    value={getFieldValue(data.DateOfAssessment)}
                    onChange={handleChange}
                    identical={compare("DateOfAssessment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormRadioButton
                    name="TypeOfAssessment"
                    label={getFieldLabel(formConfig.TypeOfAssessment)}
                    error={errors.TypeOfAssessment}
                    value={getFieldValue(data.TypeOfAssessment)}
                    options={getFieldOptions(data.TypeOfAssessment)}
                    onChange={handleChange}
                    identical={compare("TypeOfAssessment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <FormInput
                name="AssessorName"
                label={getFieldLabel(formConfig.AssessorName)}
                error={errors.AssessorName}
                value={getFieldValue(data.AssessorName)}
                onChange={handleChange}
                identical={compare("AssessorName")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="fw-bold">Instructions:</div>
              <div>
                The CANS Comprehensive is easy to learn and is well liked by
                children, youth and families, providers and other partners in
                the services system because it is easy to understand and does
                not necessarily require scoring in order to be meaningful to the
                youth and family. ★ Basic core items – grouped by domain – are
                rated for all individuals. ★ A rating of 1, 2 or 3 on key core
                questions triggers extension modules. ★ Individual assessment
                module questions provide additional information in a specific
                area.
              </div>
              <div className="fw-bold mt-3">Description:</div>
              <div>
                Standard CANS Comprehensive 2021. The Child and Adolescent Needs
                and Strengths (CANS) is a multiple purpose information
                integration tool that is designed to be the output of an
                assessment process. The purpose of the CANS Comprehensive is to
                accurately represent the shared vision of the youth/youth
                serving system—children, youth, and families. As such,
                completion of the CANS Comprehensive is accomplished in order to
                allow for the effective communication of this shared vision for
                use at all levels of the system. Since its primary purpose is
                communication, the CANS Comprehensive is designed based on
                communication theory rather than the psychometric theories that
                have influenced most measurement development. There are six key
                principles of a communimetric measure that apply to
                understanding the CANS Comprehensive.
              </div>
              <div className="clearfix mt-3 mb-2">
                <div className="float-start">
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                  <button
                    className="btn btn-primary ms-2"
                    onClick={(e) => {
                      setShowDefinitions(!showDefinitions);
                    }}
                  >
                    {showDefinitions === true
                      ? "Hide Definitions"
                      : "Show Definitions"}
                  </button>
                </div>
              </div>
              <DomainExpandDiv
                id="LifeFunctioning"
                expandStatus={expandStatus}
                title={
                  <span className="text-white">Life Functioning Domain</span>
                }
                preserveChildren={true}
                score={getScore(data, "LFD")}
                previousScore={getScore(previousAssessment, "LFD")}
                progress={getProgress("LFD")}
              >
                <div className="container bg-light">
                  {getCategoryFields("LFD").map((field) => Question(field))}
                </div>
              </DomainExpandDiv>
              <DomainExpandDiv
                id="Strengths"
                expandStatus={expandStatus}
                title={<span className="text-white">Strengths Domain</span>}
                preserveChildren={true}
                score={getScore(data, "SD")}
                previousScore={getScore(previousAssessment, "SD")}
                progress={getProgress("SD")}
              >
                <div className="container bg-light">
                  {getCategoryFields("SD").map((field) => Question(field))}
                </div>
              </DomainExpandDiv>
              <DomainExpandDiv
                id="Cultural"
                expandStatus={expandStatus}
                title={
                  <span className="text-white">Cultural Factors Domain</span>
                }
                preserveChildren={true}
                score={getScore(data, "CFD")}
                previousScore={getScore(previousAssessment, "CFD")}
                progress={getProgress("CFD")}
              >
                <div className="container bg-light">
                  {getCategoryFields("CFD").map((field) => Question(field))}
                </div>
              </DomainExpandDiv>
              <DomainExpandDiv
                id="CaregiverNeeds"
                expandStatus={expandStatus}
                title={
                  <span className="text-white">
                    Caregiver Needs and Resources Domain
                  </span>
                }
                preserveChildren={true}
                score={getScore(data, "CNRD")}
                previousScore={getScore(previousAssessment, "CNRD")}
                progress={getProgress("CNRD")}
              >
                <div className="container bg-light">
                  {getCategoryFields("CNRD").map((field) => Question(field))}
                </div>
              </DomainExpandDiv>
              <DomainExpandDiv
                id="BehavioralNeeds"
                expandStatus={expandStatus}
                title={
                  <span className="text-white">
                    Behavioral/Emotional Needs Domain
                  </span>
                }
                preserveChildren={true}
                score={getScore(data, "BEND")}
                previousScore={getScore(previousAssessment, "BEND")}
                progress={getProgress("BEND")}
              >
                <div className="container bg-light">
                  {getCategoryFields("BEND").map((field) => Question(field))}
                </div>
              </DomainExpandDiv>
              <DomainExpandDiv
                id="YouthRisk"
                expandStatus={expandStatus}
                title={
                  <span className="text-white">
                    Youth Risk Behaviors Domain
                  </span>
                }
                preserveChildren={true}
                score={getScore(data, "YRBD")}
                previousScore={getScore(previousAssessment, "YRBD")}
                progress={getProgress("YRBD")}
              >
                <div className="container bg-light">
                  {getCategoryFields("YRBD").map((field) => Question(field))}
                </div>
              </DomainExpandDiv>
              {parseInt(getFieldValue(data.LFDDevelopmental) || "0") > 0 ? (
                <DomainExpandDiv
                  id="Developmental"
                  expandStatus={expandStatus}
                  title={
                    <span className="text-white">
                      Developmental Needs (DN) Domain
                    </span>
                  }
                  preserveChildren={true}
                  score={getScore(data, "DNM")}
                  previousScore={getScore(previousAssessment, "DNM")}
                  progress={getProgress("DNM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("DNM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              {parseInt(getFieldValue(data.BENDAdjustment) || "0") > 0 ? (
                <DomainExpandDiv
                  id="Trauma"
                  expandStatus={expandStatus}
                  title={<span className="text-white">Trauma Module</span>}
                  preserveChildren={true}
                  score={getScore(data, "TM")}
                  previousScore={getScore(previousAssessment, "TM")}
                  progress={getProgress("TM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("TM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              {parseInt(getFieldValue(data.BENDSubstances) || "0") > 0 ? (
                <DomainExpandDiv
                  id="SubstanceUse"
                  expandStatus={expandStatus}
                  title={
                    <span className="text-white">
                      Substance Use Disorder (SUD) Domain
                    </span>
                  }
                  preserveChildren={true}
                  score={getScore(data, "SUDM")}
                  previousScore={getScore(previousAssessment, "SUDM")}
                  progress={getProgress("SUDM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("SUDM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              {parseInt(getFieldValue(data.YRBDDanger) || "0") > 0 ? (
                <DomainExpandDiv
                  id="Violence"
                  expandStatus={expandStatus}
                  title={<span className="text-white">Violence Module</span>}
                  preserveChildren={true}
                  score={getScore(data, "VM")}
                  previousScore={getScore(previousAssessment, "VM")}
                  progress={getProgress("VM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("VM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              {parseInt(getFieldValue(data.YRBDSexual) || "0") > 0 ? (
                <DomainExpandDiv
                  id="SexuallyAggressive"
                  expandStatus={expandStatus}
                  title={
                    <span className="text-white">
                      Sexually Aggressive Behaviors (SAB) Module
                    </span>
                  }
                  preserveChildren={true}
                  score={getScore(data, "SABM")}
                  previousScore={getScore(previousAssessment, "SABM")}
                  progress={getProgress("SABM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("SABM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              {parseInt(getFieldValue(data.YRBDRunaway) || "0") > 0 ? (
                <DomainExpandDiv
                  id="Runaway"
                  expandStatus={expandStatus}
                  title={<span className="text-white">Runaway Module</span>}
                  preserveChildren={true}
                  score={getScore(data, "RM")}
                  previousScore={getScore(previousAssessment, "RM")}
                  progress={getProgress("RM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("RM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              {parseInt(getFieldValue(data.YRBDDelinquent) || "0") > 0 ? (
                <DomainExpandDiv
                  id="Justice"
                  expandStatus={expandStatus}
                  title={
                    <span className="text-white">
                      Juvenile Justice (JJ) Module
                    </span>
                  }
                  preserveChildren={true}
                  score={getScore(data, "JJM")}
                  previousScore={getScore(previousAssessment, "JJM")}
                  progress={getProgress("JJM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("JJM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              {parseInt(getFieldValue(data.YRBDArson) || "0") > 0 ? (
                <DomainExpandDiv
                  id="FireSetting"
                  expandStatus={expandStatus}
                  title={
                    <span className="text-white">Fire Setting Module</span>
                  }
                  preserveChildren={true}
                  score={getScore(data, "FSM")}
                  previousScore={getScore(previousAssessment, "FSM")}
                  progress={getProgress("FSM")}
                >
                  <div className="container bg-light">
                    {getCategoryFields("FSM").map((field) => Question(field))}
                  </div>
                </DomainExpandDiv>
              ) : null}
              <Tooltip
                className="w-50 border-dark text-black fw-bold"
                id="definition-tooltip"
              ></Tooltip>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default ALCANS;
